import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropertyMessageCard from '../components/property/PropertyMessageCard';
import AskRiaInputBar from '../components/common/AskRiaInputBar';
import MessageStatusIndicator from '../components/common/MessageStatusIndicator';
import TypingIndicator from '../components/common/TypingIndicator';
import NotesPanel from '../components/notes/NotesPanel';
import RiaNoteLinks, { extractRiaNoteLinks } from '../components/notes/RiaNoteLinks';
import { auth, db } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { fetchLinkPreview, parseStructuredAddress, parseRialtyPropertyUrl, resolveShortUrl } from '../utils/linkPreview';
import { getViewerTimezone, formatInstantForViewer } from '../utils/dateTime';
import logo from '../assets/dashboard-logo.png';
import './ConversationPage.css';

// ── Ria AI constants ──────────────────────────────────────────────────────────
// Feature flag: set to true to show the Ria / Ria Weekly / Ria Monthly sidebar
// entries again. Kept as a single switch so the feature can be restored
// without touching any of the surrounding code.
const SHOW_RIA_SIDEBAR_ITEMS = false;

const RIA_CONVERSATION_ID = '__ria_ai__';

// Persisted (localStorage) cache of user IDs known to have no profile photo.
// The backend has no "hasPhoto" flag on the profile response, so the only way
// to know is to call /users/{id}/photo and see it 404 — but once we know,
// there's no reason to ask again on every page reload. TTL lets a later photo
// upload still get picked up eventually.
const NO_PHOTO_CACHE_KEY = 'rialty_no_photo_user_ids';
const NO_PHOTO_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

const readNoPhotoCache = () => {
  try {
    return JSON.parse(localStorage.getItem(NO_PHOTO_CACHE_KEY)) || {};
  } catch {
    return {};
  }
};

const hasNoPhotoCached = (uid) => {
  const cachedAt = readNoPhotoCache()[uid];
  return !!cachedAt && (Date.now() - cachedAt) < NO_PHOTO_CACHE_TTL_MS;
};

const markNoPhotoCached = (uid) => {
  try {
    const cache = readNoPhotoCache();
    cache[uid] = Date.now();
    localStorage.setItem(NO_PHOTO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage unavailable/full — non-critical, will just retry next load
  }
};

// Group chat avatar colors — matches mobile GROUP_CHAT_COLORS array
const GROUP_CHAT_COLORS = ['#E91E63', '#9C27B0', '#3F51B5', '#009688', '#FF5722', '#795548', '#607D8B', '#2196F3'];
const getGroupColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GROUP_CHAT_COLORS[Math.abs(hash) % GROUP_CHAT_COLORS.length];
};

const RIA_CONVERSATION = {
  id: RIA_CONVERSATION_ID,
  name: 'Ria',
  isRia: true,
  lastMessage: 'Your Real Estate Intelligent Assistant',
  time: '',
};

const RIA_WEEKLY_ID = '__ria_weekly__';
const RIA_MONTHLY_ID = '__ria_monthly__';

const RIA_WEEKLY_CONVERSATION = {
  id: RIA_WEEKLY_ID,
  name: 'Ria Weekly',
  isRia: true,
  lastMessage: 'Weekly conversation history',
  time: '',
};

const RIA_MONTHLY_CONVERSATION = {
  id: RIA_MONTHLY_ID,
  name: 'Ria Monthly',
  isRia: true,
  lastMessage: 'Monthly conversation history',
  time: '',
};


const ConversationPage = () => {
  const navigate = useNavigate();

  // SP users get their own dedicated conversations page
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() === 'SERVICE_PROVIDER') {
      navigate('/conversations/sp', { replace: true });
    }
  }, [navigate]);

  const [conversationList, setConversationList] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  // "Delete chat for me" state
  const [deleteChatConfirm, setDeleteChatConfirm] = useState(null); // { id, name } | null
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userCache, setUserCache] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'unread' | 'pinned'
  // Mobile single-pane view: true = chat pane visible, false = conversation list visible.
  // Has no effect above the mobile breakpoint (desktop/tablet always show both panes).
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  // Pinned conversation IDs — persisted to localStorage (matches mobile AsyncStorage @rialty/pinned_conversations)
  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('@rialty/pinned_conversations');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);

  // Reply / Forward / Delete — mirrors mobile UserChatScreen's selection mode
  // (long-press → checkboxes → Reply/Forward/Delete toolbar), adapted to a
  // click-to-select entry point for web.
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const [showForwardPicker, setShowForwardPicker] = useState(false);
  const [forwardingMessages, setForwardingMessages] = useState([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // New Chat panel state
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatUsers, setNewChatUsers] = useState([]);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [newChatLoading, setNewChatLoading] = useState(false);
  const [newChatLoadingMore, setNewChatLoadingMore] = useState(false);
  const [newChatHasMore, setNewChatHasMore] = useState(false);
  const newChatContactPageRef = useRef(0); // mirrors mobile currentUserPage ref
  const newChatUserListRef = useRef(null);
  const NC_PAGE_SIZE = 20; // mirrors mobile USER_PAGE_SIZE = 20
  const [selectedNewChatUsers, setSelectedNewChatUsers] = useState([]);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [showGroupNameModal, setShowGroupNameModal] = useState(false);
  const [newChatGroupName, setNewChatGroupName] = useState('');

  // New Chat — Step 1 (transaction selection) state
  const [newChatStep, setNewChatStep] = useState('start'); // 'start' | 'select'
  const [newChatTransactions, setNewChatTransactions] = useState([]);
  const [newChatTransactionsLoading, setNewChatTransactionsLoading] = useState(false);
  const [selectedNewChatTxn, setSelectedNewChatTxn] = useState(null);
  const [isBrokerUser, setIsBrokerUser] = useState(false);
  const [brokerAgentCount, setBrokerAgentCount] = useState(0);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [isBrokerageUser, setIsBrokerageUser] = useState(false);
  const [brokerageLinkedMemberCount, setBrokerageLinkedMemberCount] = useState(0);
  const [isBroadcastingBrokerage, setIsBroadcastingBrokerage] = useState(false);

  // Add member to existing chat panel state
  const [showAddMemberPanel, setShowAddMemberPanel] = useState(false);
  const [addMemberUsers, setAddMemberUsers] = useState([]);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [addedMemberIds, setAddedMemberIds] = useState([]);

  // Members modal state (view group participants)
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersData, setMembersData] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Add Contact modal state
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [addContactEmail, setAddContactEmail] = useState('');
  // 'idle' | 'loading' | 'success' | 'notfound' | 'error'
  const [addContactState, setAddContactState] = useState('idle');
  const [addContactResult, setAddContactResult] = useState(null); // { name, email }
  const [addContactError, setAddContactError] = useState('');

  // Typing indicators state (matches mobile app)
  const [typingUsers, setTypingUsers] = useState([]); // Array of {userId, userName}
  const typingTimeoutRef = useRef(null); // 3-second idle timeout before stopping typing indicator
  const typingListenerRef = useRef(null); // Firestore typing indicator unsubscribe function

  // Failed message retry queue (matches mobile chatOfflineQueue)
  const [failedMessages, setFailedMessages] = useState(new Map()); // messageId → {message, attemptCount, nextRetryAt}
  const retryTimerRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const userCacheRef = useRef({});
  const pendingProfileRequests = useRef({}); // Deduplication: track in-flight requests
  const filterDropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const linkPreviewTimeoutRef = useRef(null);
  const lastDetectedUrlRef = useRef(null);
  const linkPreviewDismissedRef = useRef(false);
  const messagesListenerRef = useRef(null);   // Firestore real-time messages unsubscribe
  // Always-fresh mirror of selectedConversation — the user_chats Firestore listener
  // (useEffect #1 below) is set up once with an empty dependency array, so its
  // handleSnapshot closure would otherwise see selectedConversation frozen at its
  // initial (null) value forever. Reading this ref instead avoids that stale read.
  const selectedConversationRef = useRef(null);
  const decryptedCacheRef = useRef(new Map()); // messageId → decrypted content cache
  const txnNameCacheRef = useRef({}); // transactionId → transactionName (mirrors mobile transactionNameCache)
  // Stores normalized ATTOM property data after URL paste (used for potential interestedProperties call)
  const propertyPreviewRef = useRef(null);
  // Ria conversation ID — in-memory only (matches mobile RiaService.conversationId)
  const riaConversationIdRef = useRef(null);
  // Ordered conversation list returned by weekly/monthly API calls
  const riaConvListRef = useRef([]);
  // Index of the next conversation to prepend when user scrolls to the top
  const riaConvIndexRef = useRef(0);
  // Tracks which duration tab is active so the scroll handler knows when to fire
  const riaActiveIdRef = useRef(null);
  const [isLoadingMoreRia, setIsLoadingMoreRia] = useState(false);

  // Mirrors mobile canManageParticipants: only AGENT / BROKER roles may add participants to non-transaction chats
  const canManageParticipants = (['AGENT', 'TRANSACTION_COORDINATOR', 'BROKER', 'BROKER_ADMIN', 'BROKER_MANAGER']
    .includes(getActiveRoleFromStorage()));

  // Helper: get Firebase token
  const getFirebaseToken = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return await currentUser.getIdToken();
  };

  // Add Contact modal helpers
  const openAddContactModal = () => {
    setAddContactEmail('');
    setAddContactState('idle');
    setAddContactResult(null);
    setAddContactError('');
    setShowAddContactModal(true);
  };
  const closeAddContactModal = () => {
    // Mirror mobile: block dismissal while the POST is in-flight
    if (addContactState === 'loading') return;
    setShowAddContactModal(false);
    setAddContactEmail('');
    setAddContactState('idle');
    setAddContactResult(null);
    setAddContactError('');
  };

  const handleAddContact = async () => {
    const email = addContactEmail.trim();
    if (!email || addContactState === 'loading') return;
    setAddContactState('loading');
    setAddContactResult(null);
    setAddContactError('');
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.post(
        API_ENDPOINTS.userContacts,
        { email },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      const data = resp.data || {};
      const name = `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || email;
      setAddContactResult({ name, email: data.email || email });
      setAddContactState('success');
    } catch (err) {
      if (err.response?.status === 404) {
        setAddContactState('notfound');
      } else {
        const msg = err.response?.data?.message || 'Could not add contact. Please try again.';
        setAddContactError(msg);
        setAddContactState('error');
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // TYPING INDICATORS (matches mobile app logic)
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Update typing status in Firestore (matches mobile updateTypingStatus)
   * Sets/removes typing indicator with current user's ID and name
   */
  const updateTypingStatus = useCallback(async (isTyping) => {
    if (!selectedConversation || activeConversation?.isRia) return;

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const typingRef = doc(db, 'chats', selectedConversation, 'typing', currentUser.uid);

      if (isTyping) {
        const userName = userCache[currentUser.uid]?.name || currentUser.displayName || 'You';
        await setDoc(typingRef, {
          userId: currentUser.uid,
          chatId: selectedConversation,
          isTyping: true,
          timestamp: serverTimestamp(),
          userName,
        });
      } else {
        await deleteDoc(typingRef);
      }
    } catch (error) {
      console.error('[Chat] Error updating typing status:', error);
      // Non-critical, don't throw
    }
  }, [selectedConversation, activeConversation, userCache]);

  /**
   * Subscribe to typing indicators for current conversation
   * Filters out stale indicators (>5 seconds old) and current user
   */
  useEffect(() => {
    if (!selectedConversation || activeConversation?.isRia) {
      if (typingListenerRef.current) {
        typingListenerRef.current();
        typingListenerRef.current = null;
      }
      setTypingUsers([]);
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const typingCollectionRef = collection(db, 'chats', selectedConversation, 'typing');

    const unsubscribe = onSnapshot(typingCollectionRef, (snapshot) => {
      const now = Date.now();
      const typing = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Exclude current user and filter stale typing indicators (> 5 seconds)
        if (data.userId !== currentUser.uid && data.isTyping) {
          const timestamp = data.timestamp?.toDate?.() || new Date(0);
          const isStale = now - timestamp.getTime() > 5000; // 5 second timeout

          if (!isStale) {
            typing.push({
              userId: data.userId,
              userName: data.userName || 'Someone',
            });
          }
        }
      });

      setTypingUsers(typing);
    }, (error) => {
      console.error('[Chat] Typing indicator listener error:', error);
    });

    typingListenerRef.current = unsubscribe;

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedConversation, activeConversation]);

  /**
   * Cleanup typing indicator when unmounting or changing conversations
   */
  useEffect(() => {
    return () => {
      updateTypingStatus(false);
    };
  }, [selectedConversation, updateTypingStatus]);

  // ────────────────────────────────────────────────────────────────────────────
  // RETRY MECHANISM (matches mobile chatOfflineQueue)
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Retry failed messages (matches mobile chatOfflineQueue.drain)
   */
  const retryFailedMessages = useCallback(async () => {
    if (failedMessages.size === 0) return;

    const now = Date.now();
    const toRetry = Array.from(failedMessages.entries())
      .filter(([_, data]) => data.nextRetryAt <= now && data.attemptCount < 8); // Max 8 attempts

    for (const [messageId, data] of toRetry) {
      try {
        const token = await getFirebaseToken();
        if (!token) continue;

        const { message } = data;

        // Update status to 'sending'
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, status: 'sending' } : m
        ));

        // Retry sending
        await apiClient.post(
          API_ENDPOINTS.chatMessages(message.conversationId),
          {
            content: message.content,
            messageType: 'text',
            metadata: {
              clientMessageId: message.clientMessageId,
              ...(message.linkPreview && { linkPreview: message.linkPreview }),
              ...(message.replyTo && { replyTo: message.replyTo }),
            },
          },
          { headers: { 'Authorization': `Bearer ${token}` }, timeout: 10000 }
        );

        // Success - remove from failed queue
        // (Firestore listener will update the message automatically)
        setFailedMessages(prev => {
          const updated = new Map(prev);
          updated.delete(messageId);
          return updated;
        });

      } catch (error) {
        // Retry failed, increase attempt count and backoff
        const attemptCount = data.attemptCount + 1;
        const backoffMs = Math.min(3000 * Math.pow(2, attemptCount), 300000); // Max 5 min

        if (attemptCount >= 8) {
          // Max attempts reached, mark as permanently failed
          setMessages(prev => prev.map(m =>
            m.id === messageId ? { ...m, status: 'failed' } : m
          ));
          setFailedMessages(prev => {
            const updated = new Map(prev);
            updated.delete(messageId);
            return updated;
          });
        } else {
          // Schedule next retry
          setFailedMessages(prev => {
            const updated = new Map(prev);
            updated.set(messageId, {
              ...data,
              attemptCount,
              nextRetryAt: Date.now() + backoffMs,
            });
            return updated;
          });
        }
      }
    }

    // Schedule next retry check
    if (failedMessages.size > 0) {
      retryTimerRef.current = setTimeout(retryFailedMessages, 15000); // Check every 15s
    }
  }, [failedMessages]);

  /**
   * Start retry timer when failed messages exist
   */
  useEffect(() => {
    if (failedMessages.size > 0 && !retryTimerRef.current) {
      retryTimerRef.current = setTimeout(retryFailedMessages, 1000);
    }

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [failedMessages, retryFailedMessages]);

  /**
   * Manual retry for failed message (user clicks "Retry")
   */
  const handleRetryMessage = useCallback(async (messageId) => {
    const failedData = failedMessages.get(messageId);
    if (!failedData) return;

    // Immediately retry
    setFailedMessages(prev => {
      const updated = new Map(prev);
      updated.set(messageId, { ...failedData, nextRetryAt: 0 });
      return updated;
    });

    retryFailedMessages();
  }, [failedMessages, retryFailedMessages]);

  // Helper function to generate initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to generate consistent color for each user
  const getAvatarColor = (name) => {
    const colors = [
      '#6c5ce7', '#00b894', '#fd79a8', '#fdcb6e',
      '#74b9ff', '#a29bfe', '#ff7675', '#55efc4',
    ];
    if (!name) return colors[0];
    const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  };

  // Helper: get message type from MIME type (matches mobile app logic)
  const getMessageType = (mimeType) => {
    if (!mimeType) return 'text';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = file.type.startsWith('image/') ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Maximum size: ${file.type.startsWith('image/') ? '10' : '25'}MB.`);
      e.target.value = '';
      return;
    }
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setPendingAttachment({ file, previewUrl, name: file.name, size: file.size, mimeType: file.type });
    e.target.value = '';
  };

  const removePendingAttachment = () => {
    if (pendingAttachment?.previewUrl) URL.revokeObjectURL(pendingAttachment.previewUrl);
    setPendingAttachment(null);
  };

  // Handle text change with URL detection — fetches real OG tags (title, image, description)
  const handleInputChange = (text) => {
    setMessageInput(text);

    // ────────── TYPING INDICATOR LOGIC ──────────
    // Clear existing typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // If user is typing, set typing status
    if (text.trim()) {
      updateTypingStatus(true);

      // Set 3-second timeout to stop typing indicator when idle
      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(false);
      }, 3000);
    } else {
      // User cleared input, stop typing immediately
      updateTypingStatus(false);
    }
    // ───────────────────────────────────────────────────

    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);

    const urlMatch = text.match(/https?:\/\/[^\s]+/);
    const detectedUrl = urlMatch ? urlMatch[0] : null;

    if (!detectedUrl) {
      if (linkPreview && !text.includes(linkPreview.url)) {
        setLinkPreview(null);
        setIsFetchingPreview(false);
        lastDetectedUrlRef.current = null;
        linkPreviewDismissedRef.current = false;
        propertyPreviewRef.current = null;
      }
      return;
    }

    if (detectedUrl !== lastDetectedUrlRef.current) linkPreviewDismissedRef.current = false;
    if (detectedUrl === lastDetectedUrlRef.current && linkPreview) return;

    linkPreviewTimeoutRef.current = setTimeout(async () => {
      if (linkPreviewDismissedRef.current) return;
      lastDetectedUrlRef.current = detectedUrl;
      setIsFetchingPreview(true);
      setLinkPreview(null);

      // Rialty property links carry the MLS ID directly — skip the generic
      // OG-tag scrape (pointless for our own domain, and would otherwise
      // fall back to a bare "rialty.ai" hostname card) and fetch the
      // property straight from the MLS-by-id endpoint instead. Mirrors
      // mobile property.service.ts's fetchPropertyFromLink: Rialty URLs
      // short-circuit past ATTOM address lookup the same way.
      const rialtyMlsId = parseRialtyPropertyUrl(detectedUrl);
      if (rialtyMlsId) {
        try {
          const token = await getFirebaseToken();
          if (token) {
            const resp = await apiClient.get(API_ENDPOINTS.mlsProperty(rialtyMlsId), {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 10000,
            });
            const d = resp.data;
            if (d) {
              const address = d.propertyAddress || d.address || d.streetAddress || d.fullAddress || '';
              const cityStateZip = [
                d.propertyCity || d.city,
                [d.propertyState || d.state, d.propertyZip || d.zipCode || d.postalCode].filter(Boolean).join(' '),
              ].filter(Boolean).join(', ');
              const price = d.listPrice || d.purchasePrice || d.price;
              const beds = d.bedrooms ?? d.beds;
              const baths = d.bathrooms ?? d.baths;
              if (!linkPreviewDismissedRef.current) {
                setLinkPreview({
                  url: detectedUrl,
                  title: [address, cityStateZip].filter(Boolean).join(', ') || 'Rialty Property',
                  description: [
                    price != null ? `$${Number(price).toLocaleString()}` : null,
                    (beds != null || baths != null) ? `${beds ?? '—'}bd/${baths ?? '—'}ba` : null,
                  ].filter(Boolean).join(' | ') || 'View property on Rialty',
                  imageUrl: d.primaryPhoto || d.photos?.[0] || null,
                  siteName: 'Rialty',
                });
              }
              propertyPreviewRef.current = {
                mlsId: rialtyMlsId,
                price: price || 0,
                address,
                city: d.propertyCity || d.city || '',
                state: d.propertyState || d.state || '',
                zipCode: d.propertyZip || d.zipCode || d.postalCode || '',
                beds: beds || 0,
                baths: baths || 0,
                sqft: d.squareFeet || d.sqft || null,
                primaryPhoto: d.primaryPhoto || d.photos?.[0] || null,
              };
            }
          }
        } catch {
          propertyPreviewRef.current = null;
        } finally {
          setIsFetchingPreview(false);
        }
        return;
      }

      try {
        const preview = await fetchLinkPreview(detectedUrl);
        if (!linkPreviewDismissedRef.current && preview) {
          setLinkPreview(preview);
        }
      } finally {
        setIsFetchingPreview(false);
      }

      // ATTOM address lookup — resolves property data from real estate URL.
      // Mirrors mobile property.service.ts's fetchPropertyFromLink / TransactionChatParties.js:
      // follow short links (redf.in, zillow.me, …) once and re-parse the canonical URL
      // before giving up, same as the working transaction-chat implementation.
      let parsed = parseStructuredAddress(detectedUrl);
      if (!parsed) {
        const canonical = await resolveShortUrl(detectedUrl);
        if (canonical !== detectedUrl) parsed = parseStructuredAddress(canonical);
      }
      if (parsed) {
        try {
          const token = await getFirebaseToken();
          if (token) {
            const address1 = parsed.street;
            let address2 = `${parsed.state} ${parsed.zip}`.trim();
            if (parsed.city) address2 = `${parsed.city}, ${address2}`;
            const attomResp = await apiClient.get(API_ENDPOINTS.attomAddressLookup, {
              headers: { 'Authorization': `Bearer ${token}` },
              params: { address1, address2 },
              timeout: 10000,
            });
            const d = attomResp.data;
            if (d) {
              propertyPreviewRef.current = {
                mlsId: d.mlsId || '',
                price: d.listPrice || 0,
                address: d.streetAddress || d.fullAddress || '',
                city: d.city || '',
                state: d.state || '',
                zipCode: d.postalCode || '',
                beds: d.bedrooms || 0,
                baths: d.bathrooms || 0,
                sqft: d.squareFeet || null,
                primaryPhoto: d.primaryPhoto || null,
                ownerName: d.ownerName || null,
              };
            }
          }
        } catch {
          propertyPreviewRef.current = null;
        }
      } else {
        propertyPreviewRef.current = null;
      }
    }, 500);
  };

  const handleDismissLinkPreview = () => {
    setLinkPreview(null);
    setIsFetchingPreview(false);
    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);
    linkPreviewDismissedRef.current = true;
    propertyPreviewRef.current = null;
  };

  // Fetch user profile from backend API and cache it
  // Fixes: deduplicates parallel requests, does NOT cache failures permanently
  const fetchUserProfile = useCallback(async (uid) => {
    if (!uid) return { name: 'Unknown', email: '', profilePhotoUrl: null };

    // Return from cache if available (only successful profiles are cached)
    if (userCacheRef.current[uid]) return userCacheRef.current[uid];

    // Deduplication: if a request for this uid is already in-flight, wait for it
    if (pendingProfileRequests.current[uid]) {
      try {
        return await pendingProfileRequests.current[uid];
      } catch {
        // If the pending request failed, continue to retry below
      }
    }

    // Create a new request promise and store it for deduplication
    const requestPromise = (async () => {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) throw new Error('No token');

      const response = await apiClient.get(
        API_ENDPOINTS.userByFirebaseUid(uid, true),
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          timeout: 10000
        }
      );

      const data = response.data;
      console.log(`[Chat] User profile for ${uid}:`, data);
      // Company-vendorType SPs have no personal firstName/lastName — only a
      // businessName — so a Company sender's messages/list entry would
      // otherwise fall through to their raw email instead of their company
      // name (same fallback added to SpConversationsPage.js's fetchUserProfile).
      const name = (data.vendorType === 'COMPANY' && data.businessName)
        ? data.businessName
        : ([data.firstName, data.lastName].filter(Boolean).join(' ') || data.name || data.displayName || data.email || 'Unknown');
      let photoUrl = data.profilePhotoBase64 || data.profilePhotoUrl || null;

      // If no base64 photo, try fetching from photo API — but skip the call
      // entirely if we already know (from a previous load) that this user has
      // no photo, so we don't keep re-triggering the same 404.
      if (!photoUrl && data.id && !hasNoPhotoCached(data.id)) {
        try {
          const photoResponse = await apiClient.get(
            API_ENDPOINTS.userPhoto(data.id),
            {
              headers: { 'Authorization': `Bearer ${firebaseToken}` },
              responseType: 'blob',
              timeout: 5000
            }
          );
          if (photoResponse.data && photoResponse.data.size > 0) {
            photoUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(photoResponse.data);
            });
          } else {
            markNoPhotoCached(data.id);
          }
        } catch (photoErr) {
          // No photo available — remember so we don't ask again next load
          markNoPhotoCached(data.id);
        }
      }

      const profile = {
        name,
        email: data.email || '',
        profilePhotoUrl: photoUrl
      };
      // Only cache successful profiles
      userCacheRef.current[uid] = profile;
      setUserCache(prev => ({ ...prev, [uid]: profile }));
      return profile;
    })();

    // Store the pending promise for deduplication
    pendingProfileRequests.current[uid] = requestPromise;

    try {
      const result = await requestPromise;
      return result;
    } catch (err) {
      const status = err?.response?.status;
      const fallbackProfile = { name: 'Unknown', email: '', profilePhotoUrl: null };
      if (status === 404) {
        // User genuinely doesn't exist on the backend (e.g. invited but not yet
        // registered) — this won't resolve on retry, so cache it. Otherwise the
        // conversation list's onSnapshot listener re-fires on every real-time
        // update and re-triggers the same failing request + log on each render.
        userCacheRef.current[uid] = fallbackProfile;
        setUserCache(prev => ({ ...prev, [uid]: fallbackProfile }));
      } else {
        console.error(`[Chat] Error fetching user profile for ${uid}:`, status, err?.message);
      }
      // Non-404 failures (network/timeout/5xx) are not cached — next call retries the API
      return fallbackProfile;
    } finally {
      // Clean up pending request so future calls can retry if it failed
      delete pendingProfileRequests.current[uid];
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch and cache transaction name from backend — mirrors mobile fetchTransactionName
  const fetchTransactionName = useCallback(async (txId) => {
    if (!txId) return null;
    if (txnNameCacheRef.current[txId] !== undefined) return txnNameCacheRef.current[txId];
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.transactionById(txId), {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 10000,
      });
      const name = res.data?.transactionName || res.data?.data?.transactionName || null;
      txnNameCacheRef.current[txId] = name;
      return name;
    } catch {
      txnNameCacheRef.current[txId] = null;
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve chat display name and profile photo.
  // Mirrors mobile ChatScreen.tsx lines 260-271:
  //   2 participants (1-on-1) → other person's name
  //   3+ participants (group) → transactionName || chatName || `Group (N)`
  const resolveChatDisplayInfo = useCallback(async (chatData, currentUid) => {
    const allParticipants = chatData.participants || [];
    const otherParticipants = allParticipants.filter(uid => uid !== currentUid);

    if (otherParticipants.length === 0) return { name: chatData.chatName || 'Chat', profilePhotoUrl: null };

    // Transaction chats are always groups regardless of participant count —
    // mirrors mobile: `if (participantCount === 2 && !isTransactionChat)` else group path
    const isTransactionChat = !!(chatData.dealId || chatData.transactionId);

    // Group chat (transaction OR 3+ total participants): transactionName || chatName || "Group (N)"
    // Mirrors mobile ChatScreen.tsx line 268: conv.transactionName || conv.chatName || `Group (N)`
    if (isTransactionChat || otherParticipants.length > 1) {
      const txId = chatData.dealId || chatData.transactionId || null;
      const transactionName = txId ? await fetchTransactionName(txId) : null;
      return {
        name: transactionName || chatData.chatName || `Group (${allParticipants.length})`,
        profilePhotoUrl: null,
      };
    }

    // 1-on-1 chat: use chatName if set, otherwise fetch the other person's profile
    if (chatData.chatName) return { name: chatData.chatName, profilePhotoUrl: null };

    const profile = await fetchUserProfile(otherParticipants[0]);
    return {
      name: profile.name || 'Chat',
      profilePhotoUrl: profile.profilePhotoUrl || null,
    };
  }, [fetchUserProfile, fetchTransactionName]);

  // Format timestamp for display
  const formatSystemMessage = (text) => {
    if (!text) return text;
    const currentUid = auth.currentUser?.uid;
    const newFmt = text.match(/^Added (.+) by (.+)$/);
    if (newFmt) {
      const addedUid = newFmt[1].trim();
      const addingUid = newFmt[2].trim();
      const addedName = userCache[addedUid]?.name || addedUid;
      const addingName = userCache[addingUid]?.name || addingUid;
      if (addedUid === currentUid) return `${addingName} added you`;
      if (addingUid === currentUid) return `You added ${addedName}`;
      return `${addingName} added ${addedName}`;
    }
    const oldFmt = text.match(/^Added (.+)$/);
    if (oldFmt) {
      const addedUid = oldFmt[1].trim();
      return `Added ${userCache[addedUid]?.name || addedUid}`;
    }
    return text;
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    const now = new Date();
    const msgDate = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    // Clamp to 0 — a message's server timestamp can land a few hundred ms
    // ahead of the local clock (server/client skew), which without clamping
    // makes Math.floor() of the small negative diff come out as -1 instead
    // of 0, rendering "-1 days ago" on a message that just arrived.
    const diffMs = Math.max(0, now - msgDate);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const zone = getViewerTimezone();
    if (diffDays === 0) {
      return formatInstantForViewer(msgDate.toISOString(), zone).timeLabel;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return msgDate.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: zone });
    }
  };

  // Fetch chat details from API
  const fetchChatDetails = async (conversationId) => {
    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) return null;

      console.log(`[Chat] Fetching chat details for ${conversationId}...`);
      const response = await apiClient.get(
        API_ENDPOINTS.chatById(conversationId),
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          timeout: 10000
        }
      );
      console.log(`[Chat] Chat details response:`, response.data);
      return response.data;
    } catch (err) {
      console.error(`[Chat] Error fetching chat details for ${conversationId}:`, err?.response?.status, err?.message);
      return null;
    }
  };

  // Fetch messages from API
  const fetchMessages = async (conversationId, limit = 50) => {
    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) return [];

      console.log(`[Chat] Fetching messages for chat ${conversationId}...`);
      const response = await apiClient.get(
        API_ENDPOINTS.chatMessages(conversationId),
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          params: { limit },
          timeout: 10000
        }
      );
      console.log(`[Chat] Messages response:`, response.data);
      return response.data;
    } catch (err) {
      console.error(`[Chat] Error fetching messages for ${conversationId}:`, err?.response?.status, err?.message);
      return [];
    }
  };

  // Cleanup messages listener on unmount
  useEffect(() => {
    return () => {
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
        messagesListenerRef.current = null;
      }
    };
  }, []);

  // Sync activeConversation when conversationList updates (e.g. after a participant is added).
  // Uses functional setter so activeConversation doesn't need to be in the dependency array.
  useEffect(() => {
    setActiveConversation(prev => {
      if (!prev || prev.isRia) return prev;
      const updated = conversationList.find(c => c.id === prev.id);
      if (!updated) return prev;
      const participantsChanged = (updated.participants?.length ?? 0) !== (prev.participants?.length ?? 0);
      const nameChanged = updated.name !== prev.name;
      if (!participantsChanged && !nameChanged) return prev;
      return { ...prev, ...updated };
    });
  }, [conversationList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep selectedConversationRef in sync — see declaration above for why this is needed.
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // useEffect #1 — Conversation list listener (Firestore)
  useEffect(() => {
    let unsubChats = null;
    let unsubAuth = null;

    unsubAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setConversationList([]);
        setLoading(false);
        setError('Please log in to view conversations.');
        return;
      }

      const uid = user.uid;
      console.log('[Chat] Authenticated user uid:', uid);
      setLoading(true);
      setError(null);

      try {
        // Mirror mobile exactly: archived === false filter + limit(100)
        const userChatsRef = query(
          collection(db, 'user_chats', uid, 'chats'),
          where('archived', '==', false),
          limit(100)
        );

        const handleSnapshot = async (snapshot) => {
          try {
            console.log(`[Chat] user_chats/${uid}/chats returned ${snapshot.docs.length} docs`);

            const chatRefs = snapshot.docs
              .map(d => ({ docId: d.id, ...d.data() }));

            console.log('[Chat] Non-archived chat refs:', chatRefs);

            const conversationsData = await Promise.all(
              chatRefs.map(async (chatRef) => {
                const chatId = chatRef.chatId || chatRef.docId;
                try {
                  const chatDoc = await getDoc(doc(db, 'chats', chatId));
                  if (!chatDoc.exists()) {
                    console.warn(`[Chat] Chat doc not found: chats/${chatId}`);
                    return null;
                  }

                  const chatData = chatDoc.data();
                  const displayInfo = await resolveChatDisplayInfo(chatData, uid);

                  const txId = chatData.dealId || chatData.transactionId || null;
                  const participantList = chatData.participants || [];
                  // Transaction chats are always groups — mirrors mobile ChatScreen.tsx line 289
                  const isGroupChat = !!txId || participantList.length > 2 || (!!chatData.chatName && participantList.length > 1);

                  return {
                    id: chatId,
                    name: displayInfo.name,
                    chatName: chatData.chatName || null,
                    initials: getInitials(displayInfo.name),
                    avatarColor: getAvatarColor(displayInfo.name),
                    groupColor: getGroupColor(displayInfo.name),
                    profilePhotoUrl: isGroupChat ? null : displayInfo.profilePhotoUrl,
                    lastMessage: chatData.lastMessagePreview || '',
                    time: formatTimestamp(chatData.lastMessageAt),
                    lastMessageAt: chatData.lastMessageAt,
                    lastMessageSenderId: chatData.lastMessageSenderId,
                    unread: chatRef.unreadCount || 0,
                    participants: participantList,
                    transactionId: txId,
                    isGroupChat,
                  };
                } catch (err) {
                  console.error(`Error fetching chat ${chatId}:`, err);
                  return null;
                }
              })
            );

            const validConversations = conversationsData
              .filter(Boolean)
              // Deduplicate by chatId first (same chat referenced twice in user_chats)
              .filter((conv, index, arr) => arr.findIndex(c => c.id === conv.id) === index)
              .sort((a, b) => {
                const timeA = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(a.lastMessageAt || 0);
                const timeB = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(b.lastMessageAt || 0);
                return timeB - timeA;
              })
              // Mirror mobile ChatScreen deduplication exactly:
              // keep only the most recent chat per first-other-participant UID
              .filter((conv, index, arr) => {
                const others = (conv.participants || []).filter(p => p !== uid);
                if (others.length === 0) return true;
                const otherUserId = others[0];
                return arr.findIndex(c => {
                  const co = (c.participants || []).filter(p => p !== uid);
                  return co.length > 0 && co[0] === otherUserId;
                }) === index;
              });

            setConversationList(validConversations);
            setLoading(false);

            // Auto-select first conversation if none selected. Reads the ref (not the
            // selectedConversation closed over when this effect first ran) so a user's
            // manual click isn't clobbered the next time this listener fires — which now
            // happens within a couple seconds of every click, since selecting a
            // conversation also marks it read (writing to this same Firestore collection).
            if (!selectedConversationRef.current && validConversations.length > 0) {
              handleConversationSelect(validConversations[0], { isAutoSelect: true });
            }

            // Retry any conversations that have "Unknown" names after a delay
            const unknownConvos = validConversations.filter(c => c.name === 'Unknown');
            if (unknownConvos.length > 0) {
              console.log(`[Chat] ${unknownConvos.length} conversations with Unknown name, retrying in 3s...`);
              setTimeout(async () => {
                let updated = false;
                const updatedList = await Promise.all(
                  validConversations.map(async (conv) => {
                    if (conv.name !== 'Unknown') return conv;
                    try {
                      const chatDoc = await getDoc(doc(db, 'chats', conv.id));
                      if (!chatDoc.exists()) return conv;
                      const chatData = chatDoc.data();
                      const displayInfo = await resolveChatDisplayInfo(chatData, uid);
                      if (displayInfo.name && displayInfo.name !== 'Unknown') {
                        updated = true;
                        return {
                          ...conv,
                          name: displayInfo.name,
                          initials: getInitials(displayInfo.name),
                          avatarColor: getAvatarColor(displayInfo.name),
                          profilePhotoUrl: displayInfo.profilePhotoUrl || conv.profilePhotoUrl,
                        };
                      }
                    } catch (retryErr) {
                      console.error(`[Chat] Retry failed for ${conv.id}:`, retryErr);
                    }
                    return conv;
                  })
                );
                if (updated) {
                  console.log('[Chat] Updated conversation names after retry');
                  setConversationList(updatedList);
                }
              }, 3000);
            }
          } catch (err) {
            console.error('Error processing chat list:', err);
            setError('Failed to load conversations.');
            setLoading(false);
          }
        };

        // Mirror mobile: pass success + error handlers to onSnapshot
        unsubChats = onSnapshot(
          userChatsRef,
          handleSnapshot,
          (err) => {
            console.error('❌ Firestore chat listener error:', err);
            setError('Failed to load conversations. Please try again.');
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Error setting up chat listener:', err);
        setError('Failed to load conversations.');
        setLoading(false);
      }
    });

    return () => {
      if (unsubAuth) unsubAuth();
      if (unsubChats) unsubChats();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time messages via Firestore — mirrors mobile subscribeToMessages.
  // Firestore fires instantly when any participant sends a message; decrypted content
  // is fetched from the REST API (backend decrypts before returning).
  const subscribeToMessages = useCallback((chatId) => {
    // Clean up previous listener
    if (messagesListenerRef.current) {
      messagesListenerRef.current();
      messagesListenerRef.current = null;
    }
    if (!chatId) return;

    decryptedCacheRef.current = new Map();
    setMessagesLoading(true);

    const decryptedCache = decryptedCacheRef.current;
    let fetchPromise = null;

    const fetchDecryptedContent = () => {
      // Share the in-flight promise so concurrent onSnapshot callbacks all wait for the
      // same fetch instead of one returning immediately with an empty cache.
      if (fetchPromise) return fetchPromise;
      fetchPromise = (async () => {
        try {
          const firebaseToken = await getFirebaseToken();
          if (!firebaseToken) return;
          const response = await apiClient.get(
            API_ENDPOINTS.chatMessages(chatId),
            { headers: { 'Authorization': `Bearer ${firebaseToken}` }, params: { limit: 100 }, timeout: 10000 }
          );
          const msgs = Array.isArray(response.data) ? response.data
            : Array.isArray(response.data?.messages) ? response.data.messages
            : Array.isArray(response.data?.data) ? response.data.data
            : [];
          msgs.forEach(msg => {
            const id = msg.messageId || msg.id;
            // Never overwrite a valid cached entry — after a participant is added the
            // backend may return "[Unable to decrypt message]" for old messages due to
            // key rotation, which would corrupt already-cached plaintext.
            if (id && msg.content && !decryptedCache.has(id)) {
              decryptedCache.set(id, msg.content);
            }
          });
        } catch (err) {
          console.warn('[Chat] Error fetching decrypted messages:', err?.message);
        } finally {
          fetchPromise = null;
        }
      })();
      return fetchPromise;
    };

    const currentUid = auth.currentUser?.uid;
    const msgsQuery = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(msgsQuery, async (snapshot) => {
      const hasNew = snapshot.docs.some(d => !decryptedCache.has(d.id));
      if (hasNew) await fetchDecryptedContent();

      const msgs = snapshot.docs
        // "Delete for me" still fully hides the message for the user who deleted it.
        // "Delete for everyone" is NOT filtered out here (mirrors mobile) — the message
        // stays in the list and renders as a dimmed "deleted" bubble instead of vanishing.
        .filter(d => {
          const data = d.data();
          return !(currentUid && data.deletedBy?.includes(currentUid));
        })
        .map(d => {
          const data = d.data();
          const isDeletedForEveryone = !!data.deletedForEveryone;
          // Firestore stores the raw encrypted bytes (or a decryption-failure placeholder
          // written by the mobile client) in data.content — only use it as a last resort
          // and only when it isn't itself the failure string.
          const isSystemMsg = (data.messageType || '').toLowerCase() === 'system';
          const rawContent = data.content !== '[Unable to decrypt message]' ? data.content : '';
          // System messages are never encrypted — skip the decryptedCache to avoid the
          // "[Unable to decrypt message]" string the backend returns for these docs.
          // Deleted-for-everyone messages never need their content resolved — the bubble
          // renders fixed text based on the flag alone (mirrors mobile MessageRow.tsx).
          const content = isDeletedForEveryone ? '' : (isSystemMsg ? (rawContent || '') : (decryptedCache.get(d.id) || rawContent || ''));
          const ts = data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null);

          // ────────── MESSAGE STATUS TRACKING (matches mobile) ──────────
          const clientMessageId = data.metadata?.clientMessageId || null;
          let status = data.status || 'sent';

          // Calculate status for sent messages based on delivery/read receipts
          if (data.senderId === currentUid) {
            const deliveredTo = data.deliveredTo || {};
            const readAt = data.readAt || {};
            const participantIds = activeConversation?.participantIds || [];
            const otherParticipants = participantIds.filter(id => id !== currentUid);

            if (otherParticipants.length > 0) {
              const allRead = otherParticipants.every(id => readAt[id]);
              const allDelivered = otherParticipants.every(id => deliveredTo[id]);

              if (allRead) status = 'read';
              else if (allDelivered) status = 'delivered';
              else status = 'sent';
            }
          }

          return {
            id: d.id,
            text: content,
            senderId: data.senderId || '',
            senderName: data.senderName || '',
            createdAt: ts?.toISOString() || '',
            isSent: data.senderId === currentUid,
            time: formatTimestamp(ts || ts?.toISOString() || ''),
            messageType: (data.messageType || 'TEXT').toUpperCase(),
            propertyData: data.metadata?.propertyData || data.propertyData || null,
            attachments: data.metadata?.attachments || data.attachments || [],
            linkPreview: data.metadata?.linkPreview || null,
            deletedForEveryone: isDeletedForEveryone,
            replyTo: data.metadata?.replyTo || null,
            forwarded: !!data.metadata?.forwarded,
            originalSenderName: data.metadata?.originalSenderName || null,
            clientMessageId,
            status,
          };
        });

      setMessages(msgs);
      // Eagerly resolve names for UIDs referenced inside system messages
      msgs.forEach(msg => {
        if (msg.messageType === 'SYSTEM' && msg.text) {
          const m = msg.text.match(/^Added (.+) by (.+)$/);
          if (m) { fetchUserProfile(m[1].trim()); fetchUserProfile(m[2].trim()); }
        }
      });
      // Resolve sender profiles for received messages that have no senderName stored
      // (mirrors mobile fetchUserDetails(senderIds) — fixes "Unknown" in group/broadcast chats)
      const uniqueSenderIds = [...new Set(
        msgs
          .filter(msg => !msg.isSent && msg.senderId && !msg.senderName && !userCacheRef.current[msg.senderId])
          .map(msg => msg.senderId)
      )];
      uniqueSenderIds.forEach(uid => fetchUserProfile(uid));
      setMessagesLoading(false);
      setTimeout(() => {
        if (messagesAreaRef.current) messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
      }, 50);
    }, (err) => {
      console.error('[Chat] Messages listener error:', err);
      setMessagesLoading(false);
    });

    messagesListenerRef.current = unsubscribe;
  }, [getFirebaseToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────── DELIVERY RECEIPT TRACKING (matches mobile) ──────────
  // Automatically mark received messages as delivered when they arrive
  useEffect(() => {
    if (!selectedConversation || !messages.length || activeConversation?.isRia) return;

    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    // Find received messages (not sent by me)
    const receivedMessageIds = messages
      .filter(msg => msg.senderId && msg.senderId !== currentUid && !msg.deletedForEveryone)
      .map(msg => msg.id);

    if (receivedMessageIds.length === 0) return;

    const markAsDelivered = async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) return;

        await apiClient.post(
          API_ENDPOINTS.chatDelivered(selectedConversation),
          { messageIds: receivedMessageIds },
          { headers: { 'Authorization': `Bearer ${token}` }, timeout: 5000 }
        );
      } catch (err) {
        // Silent fail — delivery receipts are best-effort (mirrors mobile)
        console.warn('[Chat] Failed to mark messages as delivered:', err?.message);
      }
    };

    markAsDelivered();
  }, [messages, selectedConversation, activeConversation, getFirebaseToken]);

  // ────────── READ RECEIPT TRACKING (matches mobile chatService.markMessagesAsRead) ──────────
  // Marks the conversation's most recent message as read so Firestore's per-chat
  // unreadCount (user_chats/{uid}/chats/{chatId}.unreadCount) resets to 0 — this is
  // the field both the conversation list badge and the nav-level aggregate badge read.
  const markConversationAsRead = useCallback(async (chatId) => {
    if (!chatId || chatId === RIA_CONVERSATION_ID || chatId === RIA_WEEKLY_ID || chatId === RIA_MONTHLY_ID) return;

    try {
      const lastMsgQuery = query(
        collection(db, 'chats', chatId, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(lastMsgQuery);
      if (snapshot.empty) return;
      const lastReadMessageId = snapshot.docs[0].id;

      const token = await getFirebaseToken();
      if (!token) return;

      await apiClient.post(
        API_ENDPOINTS.chatRead(chatId),
        { lastReadMessageId },
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 5000 }
      );
    } catch (err) {
      // Non-critical — mirrors mobile's silent failure for mark-as-read
      console.warn('[Chat] Failed to mark conversation as read:', err?.message);
    }
  }, [getFirebaseToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle conversation click — fetch chat details + messages from API
  // isAutoSelect=true skips opening the mobile chat pane (used only for the on-load
  // auto-select so mobile users land on the conversation list, not a chat, on first render)
  const handleConversationSelect = async (conversation, { isAutoSelect = false } = {}) => {
    setSelectedConversation(conversation.id);
    setActiveConversation(conversation);
    setMessages([]);
    setMessagesLoading(true);
    if (!isAutoSelect) setMobileChatOpen(true);

    const currentUid = auth.currentUser?.uid;

    try {
      // Step 1: Fetch chat details from API
      const chatDetails = await fetchChatDetails(conversation.id);
      if (chatDetails) {
        let displayName = conversation.name || chatDetails.chatName || 'Chat';

        // If name is still "Unknown", try re-resolving from participants
        if (displayName === 'Unknown' && chatDetails.participants?.length > 0) {
          const displayInfo = await resolveChatDisplayInfo(chatDetails, currentUid);
          if (displayInfo.name && displayInfo.name !== 'Unknown') {
            displayName = displayInfo.name;
            // Also update sidebar list
            setConversationList(prev => prev.map(c =>
              c.id === conversation.id
                ? { ...c, name: displayName, initials: getInitials(displayName), avatarColor: getAvatarColor(displayName), profilePhotoUrl: displayInfo.profilePhotoUrl || c.profilePhotoUrl }
                : c
            ));
          }
        }

        setActiveConversation(prev => ({
          ...prev,
          name: displayName,
          initials: getInitials(displayName),
          avatarColor: getAvatarColor(displayName),
          participants: chatDetails.participants || prev?.participants || [],
          lastMessage: chatDetails.lastMessagePreview || prev?.lastMessage || '',
        }));
      }

      // Step 2: Subscribe to real-time messages via Firestore listener
      subscribeToMessages(conversation.id);

      // Step 3: Mark conversation as read (fire-and-forget, mirrors mobile initializeChat)
      markConversationAsRead(conversation.id);
    } catch (err) {
      console.error('[Chat] Error loading conversation:', err);
      setMessagesLoading(false);
    }
  };

  // Handle Ria AI chat message — calls real API
  const handleSendRiaMessage = async () => {
    if (!messageInput.trim()) return;
    const text = messageInput.trim();
    setMessageInput('');

    const userMsg = {
      id: `ria-user-${Date.now()}`,
      text,
      senderId: 'user',
      isSent: true,
      time: formatInstantForViewer(new Date().toISOString()).timeLabel,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setTimeout(() => { if (messagesAreaRef.current) { messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; } }, 50);

    try {
      const token = await getFirebaseToken();
      if (!token) return;

      // Mirror mobile ChatRequest: include conversation_id (snake_case) if we have one
      const requestBody = { message: text };
      if (riaConversationIdRef.current) requestBody.conversation_id = riaConversationIdRef.current;

      const resp = await apiClient.post(
        API_ENDPOINTS.riaChat,
        requestBody,
        // Ria answers with tool calls take ~40s against the local backend
        // (fast on GKE); the 30s apiClient default aborts before they finish.
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );

      // Store conversation_id (snake_case) returned by backend — mirrors mobile storage
      if (resp.data?.conversation_id) {
        riaConversationIdRef.current = resp.data.conversation_id;
      }

      // Backend uses 'response' field (mirrors mobile ChatResponse.response)
      const reply = resp.data?.response || resp.data?.reply || resp.data?.message;
      if (reply && typeof reply === 'string') {
        const riaMsg = {
          id: resp.data?.message_id || `ria-reply-${Date.now()}`,
          text: reply,
          senderId: 'ria',
          senderName: 'Ria',
          isSent: false,
          isRia: true,
          time: formatInstantForViewer(new Date().toISOString()).timeLabel,
          createdAt: new Date().toISOString(),
          noteLinks: extractRiaNoteLinks(resp.data),
        };
        setMessages(prev => [...prev, riaMsg]);
        setTimeout(() => { if (messagesAreaRef.current) { messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; } }, 50);
      }
    } catch (err) {
      console.error('[Ria] Error sending message:', err?.message);
    }
  };

  // Send message via API POST
  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !pendingAttachment) || !selectedConversation) return;
    // Route Ria messages to the Ria handler (all three Ria tabs share the same send path)
    if (selectedConversation === RIA_CONVERSATION_ID || selectedConversation === RIA_WEEKLY_ID || selectedConversation === RIA_MONTHLY_ID) {
      handleSendRiaMessage();
      return;
    }

    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    const text = messageInput.trim();
    setMessageInput('');
    // Stop typing indicator when sending
    updateTypingStatus(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    const capturedAttachment = pendingAttachment;
    const capturedLinkPreview = linkPreview;
    // Resolved property data (Zillow/Redfin/Realtor/Rialty links, via ATTOM or
    // the MLS-by-id lookup) — sending this as a PROPERTY message instead of
    // plain text+linkPreview gives it the same rich PropertyMessageCard design
    // SharePropertyModal already uses, matching mobile's sendPropertyMessage.
    const capturedPropertyData = capturedAttachment ? null : propertyPreviewRef.current;
    // Reply target — captured then cleared immediately at send-time (mirrors mobile
    // UserChatScreen.tsx handleSend, including its latent quirk of not restoring the
    // reply link if the send later fails/is retried).
    const capturedReplyTo = replyToMessage;
    setReplyToMessage(null);
    setPendingAttachment(null);
    setLinkPreview(null);
    propertyPreviewRef.current = null;
    setIsFetchingPreview(false);
    lastDetectedUrlRef.current = null;
    linkPreviewDismissedRef.current = false;
    if (linkPreviewTimeoutRef.current) clearTimeout(linkPreviewTimeoutRef.current);

    // Truncate to 200 chars, matching mobile's ReplyToData.content truncation exactly.
    const replyToPayload = capturedReplyTo ? {
      messageId: capturedReplyTo.id,
      senderId: capturedReplyTo.senderId,
      senderName: capturedReplyTo.senderName || 'Unknown',
      content: (capturedReplyTo.text || '').substring(0, 200),
    } : null;

    // Optimistic UI: add message locally with status tracking
    const clientMessageId = `temp-${Date.now()}`;
    const optimisticMsg = {
      id: clientMessageId,
      clientMessageId,
      text,
      senderId: currentUid,
      isSent: true,
      status: 'sending', // Add status field for read receipts
      time: formatInstantForViewer(new Date().toISOString()).timeLabel,
      attachments: capturedAttachment ? [{ fileName: capturedAttachment.name, mimeType: capturedAttachment.mimeType, previewUrl: capturedAttachment.previewUrl, fileSize: capturedAttachment.size }] : [],
      linkPreview: capturedPropertyData ? null : (capturedLinkPreview || null),
      messageType: capturedPropertyData ? 'PROPERTY' : undefined,
      propertyData: capturedPropertyData || null,
      replyTo: replyToPayload,
    };
    setMessages(prev => [...prev, optimisticMsg]);

    // Optimistically move this conversation to the top of the sidebar list
    const nowTs = new Date();
    setConversationList(prev => {
      const updated = prev.map(c =>
        c.id === selectedConversation
          ? { ...c, lastMessage: text || (capturedAttachment ? capturedAttachment.name : ''), time: formatTimestamp(nowTs), lastMessageAt: nowTs }
          : c
      );
      return updated.sort((a, b) => {
        const tA = a.lastMessageAt instanceof Date ? a.lastMessageAt : (a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(a.lastMessageAt || 0));
        const tB = b.lastMessageAt instanceof Date ? b.lastMessageAt : (b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(b.lastMessageAt || 0));
        return tB - tA;
      });
    });

    // Auto-scroll
    setTimeout(() => {
      if (messagesAreaRef.current) { messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; }
    }, 50);

    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) throw new Error('Not authenticated');

      // Property link resolved during composition (Zillow/Redfin/Realtor/Rialty)
      // — send as a structured PROPERTY message so it renders as the same rich
      // PropertyMessageCard design SharePropertyModal already uses, instead of
      // the plain linkPreview card. Mirrors mobile chat.service.ts's
      // sendPropertyMessage exactly (same messageType + metadata.propertyData
      // shape as the existing SharePropertyModal POST).
      if (capturedPropertyData) {
        const priceStr = capturedPropertyData.price ? `$${Number(capturedPropertyData.price).toLocaleString()}` : '';
        const displayText = text || [capturedPropertyData.address, priceStr].filter(Boolean).join(' - ') || 'Property';
        console.log(`[Chat] Sending property message to chat ${selectedConversation}...`);
        const response = await apiClient.post(
          API_ENDPOINTS.chatMessages(selectedConversation),
          {
            content: displayText,
            messageType: 'property',
            metadata: {
              clientMessageId,
              propertyData: capturedPropertyData,
              ...(replyToPayload ? { replyTo: replyToPayload } : {})
            },
          },
          { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
        );
        console.log('[Chat] Send property message response:', response.data);
        return;
      }

      // Upload attachment first if present
      let attachments = [];
      let messageType = 'text';

      if (capturedAttachment) {
        setIsUploadingAttachment(true);
        const formData = new FormData();
        formData.append('file', capturedAttachment.file);
        const uploadResponse = await apiClient.post(
          API_ENDPOINTS.chatAttachments(selectedConversation),
          formData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              // Explicitly unset the JSON default so the browser can generate
              // the multipart Content-Type itself (with the required boundary
              // param). Setting 'multipart/form-data' here directly leaves the
              // boundary off and the backend fails to parse the body.
              'Content-Type': undefined,
            },
            timeout: 60000,
          }
        );
        setIsUploadingAttachment(false);
        attachments = [{
          fileName: uploadResponse.data.fileName || capturedAttachment.name,
          fileUrl: uploadResponse.data.fileUrl,
          fileSize: uploadResponse.data.fileSize || capturedAttachment.size,
          mimeType: uploadResponse.data.mimeType || capturedAttachment.mimeType,
        }];
        messageType = getMessageType(attachments[0].mimeType);
      }

      // POST message to API
      console.log(`[Chat] Sending message to chat ${selectedConversation}...`);
      const sendMetadata = {
        clientMessageId,
        ...(capturedLinkPreview ? { linkPreview: capturedLinkPreview } : {}),
        ...(replyToPayload ? { replyTo: replyToPayload } : {}),
      };
      const response = await apiClient.post(
        API_ENDPOINTS.chatMessages(selectedConversation),
        {
          content: text || (attachments.length > 0 ? attachments[0].fileName : ''),
          messageType,
          attachments,
          metadata: sendMetadata,
        },
        {
          headers: { 'Authorization': `Bearer ${firebaseToken}` },
          timeout: 10000
        }
      );
      console.log('[Chat] Send message response:', response.data);
      // Firestore listener picks up the new message automatically — no manual re-fetch needed
    } catch (err) {
      setIsUploadingAttachment(false);
      console.error('[Chat] Error sending message:', err?.response?.data || err?.message);

      // ────────── RETRY LOGIC ──────────
      const isRetryable = !err.response || (err.response.status >= 500);

      if (isRetryable && !capturedAttachment) {
        // Mark message as failed and add to retry queue (only for text messages, not attachments)
        setMessages(prev => prev.map(m =>
          m.id === optimisticMsg.id ? { ...m, status: 'failed' } : m
        ));

        // Add to failed queue for retry
        setFailedMessages(prev => {
          const updated = new Map(prev);
          updated.set(optimisticMsg.id, {
            message: {
              conversationId: selectedConversation,
              content: text,
              linkPreview: capturedLinkPreview,
              replyTo: capturedReplyTo,
              clientMessageId,
            },
            attemptCount: 0,
            nextRetryAt: Date.now() + 3000, // Retry in 3 seconds
          });
          return updated;
        });
      } else {
        // Non-retryable error (4xx) or attachment message - remove optimistic message and restore input
        setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
        setMessageInput(text);
        if (capturedAttachment) setPendingAttachment(capturedAttachment);
      }
      // ───────────────────────────────────
    }
  };

  // ── Reply / Forward / Delete — mirrors mobile UserChatScreen selection mode ──
  const toggleMessageSelection = (messageId) => {
    setSelectedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId); else next.add(messageId);
      return next;
    });
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
  };

  // Enters selection mode with exactly this message selected — the web equivalent
  // of mobile's long-press entry point.
  const handleStartSelection = (messageId) => {
    setSelectionMode(true);
    setSelectedMessageIds(new Set([messageId]));
  };

  const handleReply = () => {
    if (selectedMessageIds.size !== 1) return;
    const messageId = Array.from(selectedMessageIds)[0];
    const message = messages.find(m => m.id === messageId);
    if (!message) return;
    setReplyToMessage(message);
    handleCancelSelection();
  };

  const handleCancelReply = () => setReplyToMessage(null);

  const handleForward = () => {
    if (selectedMessageIds.size === 0) return;
    const selectedIds = Array.from(selectedMessageIds);
    const selectedMsgs = messages.filter(m => selectedIds.includes(m.id));
    if (selectedMsgs.length === 0) return;
    setForwardingMessages(selectedMsgs);
    handleCancelSelection();
    setShowForwardPicker(true);
  };

  const handleCancelForward = () => {
    if (isForwarding) return;
    setShowForwardPicker(false);
    setForwardingMessages([]);
  };

  // Content-only forward — attachments/link previews/property cards are deliberately
  // dropped, matching mobile's forward exactly (only msg.content survives the hop).
  const handleForwardToConversation = async (targetConversation) => {
    if (isForwarding || forwardingMessages.length === 0) return;
    setIsForwarding(true);
    try {
      const firebaseToken = await getFirebaseToken();
      if (!firebaseToken) throw new Error('Not authenticated');
      for (const msg of forwardingMessages) {
        await apiClient.post(
          API_ENDPOINTS.chatMessages(targetConversation.id),
          {
            content: msg.text || '',
            messageType: 'text',
            attachments: [],
            metadata: { forwarded: true, originalSenderName: msg.senderName || (msg.isSent ? 'You' : 'Unknown') },
          },
          { headers: { 'Authorization': `Bearer ${firebaseToken}` }, timeout: 10000 }
        );
      }
      setShowForwardPicker(false);
      setForwardingMessages([]);
    } catch (err) {
      console.error('[Chat] Error forwarding message(s):', err?.response?.data || err?.message);
      alert('Failed to forward message. Please try again.');
    } finally {
      setIsForwarding(false);
    }
  };

  const handleDeleteMessagesClick = () => {
    if (selectedMessageIds.size === 0) return;
    setShowDeleteConfirm(true);
  };

  // "Delete chat for me" — mirrors main mobile ChatScreen.handleDeleteConversation.
  // Backend POST /api/v1/chats/{id}/hide sets user_chats/{uid}/chats/{id}.archived = true;
  // the ConversationPage subscription filters `archived == false`, so the chat drops
  // out of the list automatically. Other participant is unaffected.
  const requestDeleteChat = (conv, e) => {
    if (e) e.stopPropagation();
    setDeleteChatConfirm({
      id: conv.conversationId || conv.id,
      name: conv.name || conv.chatName || 'this chat',
    });
  };
  const closeDeleteChatConfirm = () => { if (!isDeletingChat) setDeleteChatConfirm(null); };
  const confirmDeleteChat = async () => {
    if (!deleteChatConfirm) return;
    const chatId = deleteChatConfirm.id;
    setIsDeletingChat(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.chatHide(chatId),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Auto-deselect if the deleted chat was open. The Firestore listener
      // will drop it from the sidebar list on the next snapshot.
      if (selectedConversation === chatId) {
        setSelectedConversation(null);
      }
      setDeleteChatConfirm(null);
    } catch (err) {
      console.error('Failed to hide chat:', err);
      window.alert('Failed to delete chat. Please try again.');
    } finally {
      setIsDeletingChat(false);
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setShowDeleteConfirm(false);
  };

  // All-or-nothing eligibility for "Delete for Everyone": every selected message must
  // be the current user's own AND sent within the last 48 hours — mirrors mobile's
  // canDeleteForEveryone exactly (a single ineligible message hides the option entirely).
  const canDeleteSelectedForEveryone = () => {
    const currentUid = auth.currentUser?.uid;
    if (!currentUid || selectedMessageIds.size === 0) return false;
    const selectedIds = Array.from(selectedMessageIds);
    const selectedMsgs = messages.filter(m => selectedIds.includes(m.id));
    if (selectedMsgs.length === 0) return false;
    const allMessagesBySender = selectedMsgs.every(m => m.senderId === currentUid);
    const now = Date.now();
    const fortyEightHours = 48 * 60 * 60 * 1000;
    const allMessagesRecent = selectedMsgs.every(m => {
      if (!m.createdAt) return false;
      const t = new Date(m.createdAt).getTime();
      return !isNaN(t) && (now - t < fortyEightHours);
    });
    return allMessagesBySender && allMessagesRecent;
  };

  // Mirrors mobile chat.service.ts updateLastMessagePreview — re-derives the
  // conversation list's shared preview from the single most recent message after a
  // delete. Non-critical: failures are swallowed, matching mobile exactly.
  const updateLastMessagePreview = async (conversationId) => {
    try {
      const msgsQuery = query(
        collection(db, 'chats', conversationId, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(msgsQuery);
      const chatRef = doc(db, 'chats', conversationId);
      if (snapshot.empty) {
        await updateDoc(chatRef, { lastMessagePreview: '', lastMessageAt: null });
        return;
      }
      const lastMessageData = snapshot.docs[0].data();
      if (lastMessageData.deletedForEveryone) {
        await updateDoc(chatRef, {
          lastMessagePreview: 'This message was deleted',
          lastMessageAt: lastMessageData.timestamp,
          lastMessageSenderId: lastMessageData.senderId || '',
        });
      } else {
        const content = lastMessageData.content || '';
        const preview = content.length > 100 ? content.substring(0, 100) + '...' : content;
        await updateDoc(chatRef, { lastMessagePreview: preview, lastMessageAt: lastMessageData.timestamp });
      }
    } catch (err) {
      console.error('[Chat] updateLastMessagePreview failed (non-critical):', err?.message);
    }
  };

  // mode: 'me' | 'everyone' — both are direct Firestore batch writes (not REST calls),
  // matching mobile's deleteMessagesForMe/deleteMessagesForEveryone exactly.
  const handleConfirmDelete = async (mode) => {
    const messageIds = Array.from(selectedMessageIds);
    const currentUid = auth.currentUser?.uid;
    if (messageIds.length === 0 || !currentUid || !selectedConversation) {
      setShowDeleteConfirm(false);
      return;
    }
    setIsDeleting(true);
    try {
      const batch = writeBatch(db);
      messageIds.forEach(messageId => {
        const messageRef = doc(db, 'chats', selectedConversation, 'messages', messageId);
        if (mode === 'everyone') {
          batch.update(messageRef, {
            deletedForEveryone: true,
            content: 'This message was deleted',
            deletedAt: serverTimestamp(),
          });
        } else {
          batch.update(messageRef, { deletedBy: arrayUnion(currentUid) });
        }
      });
      await batch.commit();
      await updateLastMessagePreview(selectedConversation);
      setShowDeleteConfirm(false);
      handleCancelSelection();
    } catch (err) {
      console.error('[Chat] Error deleting messages:', err?.message);
      alert('Failed to delete messages. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper: get date label for message date separators — bucketed in the
  // viewer's own profile timezone, not the device's, so the divider lands on
  // the same day the timestamp itself is rendered on.
  const getDateLabel = (dateStr) => {
    if (!dateStr) return null;
    const msgDate = new Date(dateStr);
    if (isNaN(msgDate)) return null;
    const zone = getViewerTimezone();
    const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: zone });
    const todayKey = dayKey(new Date());
    const msgKey = dayKey(msgDate);
    if (msgKey === todayKey) return 'Today';
    if (dayKey(new Date(Date.now() - 86400000)) === msgKey) return 'Yesterday';
    return msgDate.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: zone });
  };

  // Click-outside — filter dropdown
  useEffect(() => {
    const handler = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  // Toggle pin — persisted to localStorage (matches mobile togglePin / AsyncStorage)
  const togglePin = (e, convId) => {
    e.stopPropagation();
    setPinnedIds(prev => {
      const next = new Set(prev);
      next.has(convId) ? next.delete(convId) : next.add(convId);
      localStorage.setItem('@rialty/pinned_conversations', JSON.stringify([...next]));
      return next;
    });
  };

  // Open Ria AI chat — mirrors mobile RiaService.loadLatestConversation()
  const handleRiaChatSelect = async () => {
    setSelectedConversation(RIA_CONVERSATION_ID);
    setActiveConversation(RIA_CONVERSATION);
    setMessages([]);
    setMessagesLoading(true);
    setMobileChatOpen(true);
    riaConvListRef.current = [];
    riaConvIndexRef.current = 0;
    riaActiveIdRef.current = null;

    try {
      const token = await getFirebaseToken();
      if (!token) { setMessagesLoading(false); return; }

      // Step 1: GET /api/v1/ria/conversations?limit=1&duration=all
      // duration=all: fetch the true latest conversation regardless of age,
      // not just one from the last 7 days (default filter is date-scoped).
      const listResp = await apiClient.get(API_ENDPOINTS.riaConversations, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1, duration: 'all' },
      });
      const conversations = listResp.data?.conversations || [];
      if (conversations.length === 0) { setMessagesLoading(false); return; }

      // Step 2: GET /api/v1/ria/conversations/{id}
      const latestConvId = conversations[0].id;
      const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(latestConvId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const conv = detailResp.data?.conversation;
      if (!conv) { setMessagesLoading(false); return; }

      riaConversationIdRef.current = conv.id;

      // Map backend messages to web message shape (mirrors mobile conv.messages.map)
      const normalized = (conv.messages || []).map((msg) => ({
        id: msg.id,
        text: msg.content,
        senderId: msg.role === 'user' ? 'user' : 'ria',
        senderName: msg.role === 'user' ? undefined : 'Ria',
        isSent: msg.role === 'user',
        isRia: msg.role !== 'user',
        time: msg.created_at
          ? formatInstantForViewer(msg.created_at).timeLabel
          : '',
        createdAt: msg.created_at || new Date().toISOString(),
        noteLinks: msg.role === 'user' ? undefined : extractRiaNoteLinks(msg),
      }));
      setMessages(normalized);
      setTimeout(() => { if (messagesAreaRef.current) { messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; } }, 50);
    } catch (err) {
      console.warn('[Ria] Failed to load conversation history:', err?.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Fetch and normalise messages from a single Ria conversation by ID
  const fetchRiaConvMessages = async (convId, token) => {
    const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(convId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const conv = detailResp.data?.conversation;
    if (!conv) return [];
    riaConversationIdRef.current = conv.id;
    return (conv.messages || []).map((msg) => ({
      id: msg.id,
      text: msg.content,
      senderId: msg.role === 'user' ? 'user' : 'ria',
      senderName: msg.role === 'user' ? undefined : 'Ria',
      isSent: msg.role === 'user',
      isRia: msg.role !== 'user',
      time: msg.created_at
        ? formatInstantForViewer(msg.created_at).timeLabel
        : '',
      createdAt: msg.created_at || new Date().toISOString(),
      noteLinks: msg.role === 'user' ? undefined : extractRiaNoteLinks(msg),
    }));
  };

  // Generic handler for Ria Weekly / Monthly tabs
  // apiParams is passed directly as axios params (e.g. { duration: 'weekly' })
  const handleRiaDurationSelect = async (tabId, convObj, apiParams) => {
    setSelectedConversation(tabId);
    setActiveConversation(convObj);
    setMessages([]);
    setMessagesLoading(true);
    setMobileChatOpen(true);
    riaConvListRef.current = [];
    riaConvIndexRef.current = 0;
    riaActiveIdRef.current = tabId;

    try {
      const token = await getFirebaseToken();
      if (!token) { setMessagesLoading(false); return; }

      const listResp = await apiClient.get(API_ENDPOINTS.riaConversations, {
        headers: { Authorization: `Bearer ${token}` },
        params: apiParams,
      });
      const conversations = listResp.data?.conversations || [];
      riaConvListRef.current = conversations;

      if (conversations.length === 0) { setMessagesLoading(false); return; }

      // Load the most-recent conversation immediately
      const normalized = await fetchRiaConvMessages(conversations[0].id, token);
      riaConvIndexRef.current = 1;
      setMessages(normalized);
      setTimeout(() => {
        if (messagesAreaRef.current) {
          messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
        }
      }, 50);
    } catch (err) {
      console.warn('[Ria] Failed to load conversation history:', err?.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleRiaWeeklySelect = () =>
    handleRiaDurationSelect(RIA_WEEKLY_ID, RIA_WEEKLY_CONVERSATION, { duration: 'weekly' });

  const handleRiaMonthlySelect = () =>
    handleRiaDurationSelect(RIA_MONTHLY_ID, RIA_MONTHLY_CONVERSATION, { duration: 'monthly', limit: 50 });

  // Scroll handler for the messages area — loads older conversations on scroll to top
  // (mirrors mobile FlatList onEndReached pattern, inverted for upward history)
  const handleMessagesScroll = async (e) => {
    const el = e.currentTarget;
    if (!riaActiveIdRef.current) return; // Only active for weekly / monthly tabs
    if (el.scrollTop > 80) return; // Trigger only near the top
    if (isLoadingMoreRia) return;
    const nextIdx = riaConvIndexRef.current;
    if (nextIdx >= riaConvListRef.current.length) return;

    setIsLoadingMoreRia(true);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const prevScrollHeight = el.scrollHeight;
      const msgs = await fetchRiaConvMessages(riaConvListRef.current[nextIdx].id, token);
      riaConvIndexRef.current = nextIdx + 1;
      // Prepend older messages and restore scroll position so the view doesn't jump
      setMessages(prev => [...msgs, ...prev]);
      setTimeout(() => {
        if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
      }, 50);
    } catch (err) {
      console.warn('[Ria] Failed to load older conversation:', err?.message);
    } finally {
      setIsLoadingMoreRia(false);
    }
  };

  // ── New Chat panel ────────────────────────────────────────────────────────────

  // ── Add member to existing chat ──────────────────────────────────────────────

  // Transaction-scoped visibility rules (mirrors mobile AddUserToChatScreen)
  const getChatVisibleParticipants = (participants, myRole) => {
    if (!myRole) return participants;
    const buyerSide = ['BUYER', 'BUYERS_AGENT', 'CO_BUYERS_AGENT', 'TRANSACTION_COORDINATOR'];
    const sellerSide = ['SELLER', 'LISTING_AGENT', 'CO_LISTING_AGENT'];
    switch (myRole) {
      case 'BUYER':   return participants.filter(p => buyerSide.includes(p.role));
      case 'SELLER':  return participants.filter(p => sellerSide.includes(p.role));
      case 'BUYERS_AGENT':
      case 'CO_BUYERS_AGENT':  return participants.filter(p => p.role !== 'SELLER');
      case 'LISTING_AGENT':
      case 'CO_LISTING_AGENT': return participants.filter(p => p.role !== 'BUYER');
      default: return participants;
    }
  };

  const openAddMemberPanel = async () => {
    if (!activeConversation) return;
    // Only agents/brokers may add participants; transaction chats and broadcast chats are excluded (mirrors mobile)
    if (!canManageParticipants || activeConversation.transactionId || activeConversation.name === 'Broadcast Message' || activeConversation.name === 'Brokerage Broadcast') return;
    setShowAddMemberPanel(true);
    setAddMemberSearch('');
    setAddedMemberIds([]);
    setAddMemberUsers([]);
    setAddMemberLoading(true);

    try {
      const token = await getFirebaseToken();
      if (!token) return;

      const currentUid = auth.currentUser?.uid;
      const currentEmail = (JSON.parse(localStorage.getItem('user') || '{}')).email || '';
      const existingParticipants = activeConversation.participants || [];

      if (activeConversation.transactionId) {
        const resp = await apiClient.get(
          API_ENDPOINTS.transactionParticipants(activeConversation.transactionId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const pData = resp.data;
        let raw = Array.isArray(pData) ? pData
          : pData?.content ?? pData?.data?.content ?? pData?.data ?? pData?.participants ?? [];

        const myParticipant = raw.find(p =>
          (p.firebaseUid || p.userId) === currentUid
        );
        const myRole = myParticipant?.role || null;
        const visible = getChatVisibleParticipants(raw, myRole);

        const users = visible
          .filter(p => {
            const uid = p.firebaseUid || p.userId || p.id;
            return uid && uid !== currentUid && !existingParticipants.includes(uid);
          })
          .map(p => ({
            id: p.firebaseUid || p.userId || p.id,
            name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown',
            email: p.email || '',
            role: (p.role || '').replace(/_/g, ' '),
            profilePhotoUrl: p.profilePhotoBase64 || p.profilePhotoUrl || null,
          }));
        setAddMemberUsers(users);
      } else {
        // No transaction context — load the caller's contact graph. GET
        // /api/v1/users/me/contacts returns every contact regardless of role
        // (no role-based visibility restriction) — mirrors mobile's
        // AddUserToChatScreen / contactService.getContacts(), so no
        // client-side role filter is applied here either.
        const myRole = getActiveRoleFromStorage();
        const resp = await apiClient.get(API_ENDPOINTS.usersContacts, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: 0,
            size: 100,
            ...(myRole && { viewingRole: myRole }),
          },
        });
        const d = resp.data;
        const content = Array.isArray(d) ? d : (d.content || d.data || []);

        const users = content
          .filter(u => {
            const uid = u.firebaseUid || u.uid || u.id;
            if (!uid || uid === currentUid) return false;
            if (currentEmail && u.email && u.email.toLowerCase() === currentEmail.toLowerCase()) return false;
            if (existingParticipants.includes(uid)) return false;
            return true;
          })
          .map(u => ({
            id: u.firebaseUid || u.uid || u.id,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.name || u.displayName || u.email || 'Unknown',
            email: u.email || '',
            role: (u.role || (Array.isArray(u.roles) && u.roles[0]) || '').replace(/_/g, ' '),
            profilePhotoUrl: u.profilePhotoBase64 || u.profilePhotoUrl || null,
          }));
        setAddMemberUsers(users);
      }
    } catch (err) {
      console.error('[Chat] Error loading users for add member:', err);
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleAddMember = async (user) => {
    if (isAddingMember) return;
    setIsAddingMember(true);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      await apiClient.post(
        API_ENDPOINTS.chatParticipants(activeConversation.id),
        { participantIds: [user.id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddedMemberIds(prev => [...prev, user.id]);
      setAddMemberSearch('');
    } catch (err) {
      console.error('[Chat] Error adding member:', err);
      alert(`Failed to add ${user.name}. Please try again.`);
    } finally {
      setIsAddingMember(false);
    }
  };

  // Open members modal and resolve participant profiles
  const openMembersModal = async () => {
    if (!activeConversation) return;
    setShowMembersModal(true);
    setMembersLoading(true);
    setMembersData([]);
    try {
      const participantIds = activeConversation.participants || [];
      const profiles = await Promise.all(participantIds.map(uid => fetchUserProfile(uid)));
      setMembersData(participantIds.map((uid, i) => ({ uid, ...profiles[i] })));
    } catch (err) {
      console.error('[Chat] Error loading members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  // Step 1: open panel and load transactions + broker agent count
  const openNewChat = async () => {
    setShowNewChat(true);
    setNewChatStep('start');
    setSelectedNewChatUsers([]);
    setNewChatSearch('');
    setNewChatGroupName('');
    setSelectedNewChatTxn(null);
    setNewChatTransactions([]);
    setNewChatTransactionsLoading(true);
    setBrokerAgentCount(0);

    try {
      const token = await getFirebaseToken();
      if (!token) return;

      // Determine if the logged-in user is a Broker or Brokerage
      const activeRole = getActiveRoleFromStorage();
      const broker = activeRole === 'BROKER';
      const brokerage = activeRole === 'BROKERAGE';
      setIsBrokerUser(broker);
      setIsBrokerageUser(brokerage);

      // Fetch active transactions for the list
      const txnUrl = `${API_ENDPOINTS.transactions}?status=ACTIVE&status=UNDER_CONTRACT&status=CLOSING&status=PENDING&sortBy=createdAt&sortOrder=desc`;
      const txnResp = await apiClient.get(txnUrl, { headers: { Authorization: `Bearer ${token}` } });
      const txnData = txnResp.data;
      let txns = [];
      if (Array.isArray(txnData)) txns = txnData;
      else if (txnData?.content && Array.isArray(txnData.content)) txns = txnData.content;
      else if (txnData?.data?.content && Array.isArray(txnData.data.content)) txns = txnData.data.content;
      else if (txnData?.data && Array.isArray(txnData.data)) txns = txnData.data;
      setNewChatTransactions(txns);

      // If Broker: fetch agent count for Broadcast to Team section
      if (broker) {
        try {
          const cntResp = await apiClient.get(API_ENDPOINTS.brokerAgentCount, { headers: { Authorization: `Bearer ${token}` } });
          const cnt = typeof cntResp.data === 'number' ? cntResp.data : (cntResp.data?.count || cntResp.data?.agentCount || 0);
          setBrokerAgentCount(cnt);
        } catch { setBrokerAgentCount(0); }
      }

      // If Brokerage: fetch linked member count (brokers + agents) for Brokerage Broadcast section
      if (brokerage) {
        try {
          const cntResp = await apiClient.get(API_ENDPOINTS.brokerageLinkedMemberCount, { headers: { Authorization: `Bearer ${token}` } });
          const cnt = typeof cntResp.data === 'number' ? cntResp.data : (cntResp.data?.count || cntResp.data?.memberCount || 0);
          setBrokerageLinkedMemberCount(cnt);
        } catch { setBrokerageLinkedMemberCount(0); }
      }
    } catch (err) {
      console.error('Error loading data for new chat:', err);
    } finally {
      setNewChatTransactionsLoading(false);
    }
  };

  // Paginated contacts loader for General Chat — mirrors mobile loadUsers(page)
  // Backend returns every contact regardless of role; viewingRole is sent for logging only
  const loadContactsPage = async (page, existingToken) => {
    if (page > 0) setNewChatLoadingMore(true);
    try {
      const token = existingToken || await getFirebaseToken();
      const currentUid = auth.currentUser?.uid;
      const currentEmail = (() => {
        try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
      })();
      const viewingRole = getActiveRoleFromStorage();
      const resp = await apiClient.get(API_ENDPOINTS.userContacts, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          size: NC_PAGE_SIZE,
          ...(viewingRole ? { viewingRole } : {}),
        },
      });
      const d = resp.data;
      const content = Array.isArray(d) ? d : (d.content || d.data || d.contacts || []);
      const totalPages = d.totalPages || 1;
      const filtered = content.filter(u => {
        const uid = u.firebaseUid || u.uid || u.id;
        if (uid && uid === currentUid) return false;
        if (currentEmail && u.email && u.email.toLowerCase() === currentEmail.toLowerCase()) return false;
        return true;
      });
      if (page === 0) {
        setNewChatUsers(filtered);
      } else {
        setNewChatUsers(prev => [...prev, ...filtered]);
      }
      newChatContactPageRef.current = page;
      setNewChatHasMore(page + 1 < totalPages);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setNewChatLoadingMore(false);
    }
  };

  // Scroll handler — triggers next page when within 30% of the list bottom
  // mirrors mobile onEndReachedThreshold={0.3}
  const handleUserListScroll = () => {
    const el = newChatUserListRef.current;
    if (!el || newChatLoadingMore || !newChatHasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - el.scrollHeight * 0.3) {
      loadContactsPage(newChatContactPageRef.current + 1);
    }
  };

  // Step 2: user selects a transaction (or null = General Chat) → load participants
  const selectTransactionForChat = async (txn) => {
    setSelectedNewChatTxn(txn);
    setNewChatStep('select');
    setSelectedNewChatUsers([]);
    setNewChatSearch('');
    setNewChatLoading(true);

    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const currentUid = auth.currentUser?.uid;
      const currentEmail = (JSON.parse(localStorage.getItem('user') || '{}')).email || '';

      if (txn) {
        // Transaction chat — load only transaction participants
        const resp = await apiClient.get(
          API_ENDPOINTS.transactionParticipants(txn.id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const pData = resp.data;
        let participants = [];
        if (Array.isArray(pData)) participants = pData;
        else if (pData?.content && Array.isArray(pData.content)) participants = pData.content;
        else if (pData?.data && Array.isArray(pData.data)) participants = pData.data;
        else if (pData?.participants && Array.isArray(pData.participants)) participants = pData.participants;

        const users = participants
          .filter(p => {
            const uid = p.firebaseUid || p.userId || p.id;
            return uid && uid !== currentUid;
          })
          .map(p => {
            const nameParts = (p.name || '').trim().split(/\s+/);
            return {
              id: p.userId || p.id || p.firebaseUid,
              firebaseUid: p.firebaseUid || p.userId || p.id,
              firstName: p.firstName || nameParts[0] || '',
              lastName: p.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
              email: p.email || '',
              roles: p.roles || (p.role ? [p.role] : []),
            };
          });
        setNewChatUsers(users);
      } else {
        // General chat — load paginated contacts with viewingRole (mirrors mobile loadUsers(0))
        newChatContactPageRef.current = 0;
        setNewChatHasMore(false);
        await loadContactsPage(0, token);
      }
    } catch (err) {
      console.error('Error loading participants for chat:', err);
    } finally {
      setNewChatLoading(false);
    }
  };

  // Shared broadcast helper: create or open a named group broadcast chat
  const composeBroadcast = async ({ chatName, fetchParticipantIds, setLoading, emptyMessage }) => {
    setLoading(true);
    try {
      const token = await getFirebaseToken();
      const currentUid = auth.currentUser?.uid;
      if (!token || !currentUid) return;

      const participantIds = await fetchParticipantIds(token);

      if (participantIds.length === 0) {
        alert(emptyMessage);
        return;
      }

      const allParticipants = [currentUid, ...participantIds.filter(id => id !== currentUid)];
      const resp = await apiClient.post(API_ENDPOINTS.createChat, {
        participants: allParticipants,
        chatName,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const chatId = resp.data?.chatId || resp.data?.id;
      closeNewChat();
      if (chatId) {
        setTimeout(() => {
          setConversationList(prev => {
            const found = prev.find(c => c.id === chatId);
            if (found) handleConversationSelect(found);
            return prev;
          });
        }, 1500);
      }
    } catch (err) {
      console.error(`Error creating ${chatName}:`, err);
      alert('Failed to create broadcast. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Broker only: create/open Broadcast Message group chat
  const handleComposeBroadcast = () =>
    composeBroadcast({
      chatName: 'Broadcast Message',
      fetchParticipantIds: async (token) => {
        const idsResp = await apiClient.get(API_ENDPOINTS.brokerAgentIds, { headers: { Authorization: `Bearer ${token}` } });
        const raw = idsResp.data;
        return Array.isArray(raw) ? raw : (raw?.data || raw?.ids || raw?.agentIds || []);
      },
      setLoading: setIsBroadcasting,
      emptyMessage: 'No agents found in your brokerage.',
    });

  // Brokerage only: create/open Brokerage Broadcast group chat with all linked members (brokers + agents)
  const handleComposeBrokerageBroadcast = () =>
    composeBroadcast({
      chatName: 'Brokerage Broadcast',
      fetchParticipantIds: async (token) => {
        const idsResp = await apiClient.get(API_ENDPOINTS.brokerageLinkedMemberIds, { headers: { Authorization: `Bearer ${token}` } });
        const raw = idsResp.data;
        return Array.isArray(raw) ? raw : (raw?.data || raw?.ids || raw?.memberIds || []);
      },
      setLoading: setIsBroadcastingBrokerage,
      emptyMessage: 'No brokers or agents are linked to your firm.',
    });

  const closeNewChat = () => {
    setShowNewChat(false);
    setNewChatStep('start');
    setNewChatUsers([]);
    setSelectedNewChatUsers([]);
    setNewChatSearch('');
    setShowGroupNameModal(false);
    setNewChatGroupName('');
    setSelectedNewChatTxn(null);
    setNewChatTransactions([]);
    setBrokerAgentCount(0);
    setIsBroadcasting(false);
    setBrokerageLinkedMemberCount(0);
    setIsBroadcastingBrokerage(false);
  };

  const handleToggleNewChatUser = (user) => {
    const id = user.firebaseUid || user.id;
    setSelectedNewChatUsers(prev => {
      const exists = prev.some(u => (u.firebaseUid || u.id) === id);
      return exists ? prev.filter(u => (u.firebaseUid || u.id) !== id) : [...prev, user];
    });
  };

  const handleStartNewChat = () => {
    if (selectedNewChatUsers.length === 0 || isCreatingChat) return;
    if (selectedNewChatUsers.length > 1) {
      setShowGroupNameModal(true);
    } else {
      doCreateChat('');
    }
  };

  const doCreateChat = async (chatName) => {
    setIsCreatingChat(true);
    setShowGroupNameModal(false);
    try {
      const token = await getFirebaseToken();
      const currentUid = auth.currentUser?.uid;
      const participantUids = selectedNewChatUsers.map(u => u.firebaseUid || u.id).filter(Boolean);
      const allParticipants = [currentUid, ...participantUids.filter(id => id !== currentUid)];

      const payload = { participants: allParticipants };
      if (chatName && chatName.trim()) payload.chatName = chatName.trim();
      if (selectedNewChatTxn?.id) payload.dealId = selectedNewChatTxn.id;

      const resp = await apiClient.post(API_ENDPOINTS.createChat, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const chatId = resp.data?.chatId || resp.data?.id;
      closeNewChat();

      // After Firestore listener updates the list, select the new chat
      if (chatId) {
        setTimeout(() => {
          setConversationList(prev => {
            const found = prev.find(c => c.id === chatId);
            if (found) handleConversationSelect(found);
            return prev;
          });
        }, 1500);
      }
    } catch (err) {
      console.error('Error creating chat:', err);
      alert('Failed to create chat. Please try again.');
    } finally {
      setIsCreatingChat(false);
      setNewChatGroupName('');
    }
  };

  const filteredNewChatUsers = newChatUsers.filter(user => {
    if (!newChatSearch.trim()) return true;
    const q = newChatSearch.toLowerCase();
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    return fullName.includes(q) || (user.email || '').toLowerCase().includes(q);
  });

  const filteredAddMemberUsers = addMemberUsers.filter(user => {
    if (addedMemberIds.includes(user.id)) return false;
    if (!addMemberSearch.trim()) return true;
    const q = addMemberSearch.toLowerCase();
    return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
  });

  // Filter + sort conversations: pinned first, then by recency (matches mobile ChatScreen)
  const filteredConversations = conversationList
    .filter(conv => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = (conv.name && conv.name.toLowerCase().includes(q)) ||
          (conv.lastMessage && conv.lastMessage.toLowerCase().includes(q));
        if (!matchesSearch) return false;
      }
      if (activeFilter === 'unread') return conv.unread > 0;
      if (activeFilter === 'pinned') return pinnedIds.has(conv.id);
      return true;
    })
    .sort((a, b) => {
      // Pinned conversations always appear first (matches mobile sort logic)
      const aPinned = pinnedIds.has(a.id);
      const bPinned = pinnedIds.has(b.id);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return 0; // preserve existing recency order from conversationList
    });

  return (
    <div className="conversation-page">

      <div className={`conversation-container${mobileChatOpen ? ' mobile-chat-open' : ''}`}>
        {/* Left Sidebar - Conversation List */}
        <div className="conversation-sidebar">
          {showNewChat ? (
            /* ── New Chat Panel (2-step flow matching mobile StartNewChatScreen → NewChatScreen) ── */
            <div className="new-chat-panel">

              {newChatStep === 'start' ? (
                /* ── STEP 1: Select Transaction ── */
                <>
                  <div className="new-chat-header">
                    <button className="new-chat-back-btn" onClick={closeNewChat} title="Back">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </button>
                    <h2 className="new-chat-title">New Chat</h2>
                  </div>

                  {/* Chat Rules Banner */}
                  <div className="nc-rules-banner">
                    <div className="nc-rules-title">Chat Rules:</div>
                    <ul className="nc-rules-list">
                      <li>Select 1 or more participants</li>
                      <li>Cannot remove people once chat is created</li>
                      <li>Click &ldquo;Start Chat&rdquo; to create new chat</li>
                    </ul>
                  </div>

                  {/* Broadcast to Team — Broker only */}
                  {isBrokerUser && (
                    <div className="nc-broadcast-section">
                      <div className="nc-broadcast-info-row">
                        <div className="nc-broadcast-icon-wrap">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0">
                            <path d="M18 11v-1a2 2 0 0 0-2-2H4l-2 6h16a2 2 0 0 0 2-2v-1zM4 11h10M3 15l1-4"/>
                            <path d="M22 5L2 12.5" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="19" cy="5" r="3" fill="white"/>
                          </svg>
                        </div>
                        <div className="nc-broadcast-text">
                          <div className="nc-broadcast-title">Broadcast to Team</div>
                          <div className="nc-broadcast-subtitle">
                            Send a message to all team members in your brokerage
                          </div>
                        </div>
                      </div>
                      <button className="nc-broadcast-btn" onClick={handleComposeBroadcast} disabled={isBroadcasting}>
                        {isBroadcasting ? 'Creating...' : 'Compose Broadcast'}
                      </button>
                    </div>
                  )}

                  {/* Brokerage Broadcast — Brokerage role only */}
                  {isBrokerageUser && brokerageLinkedMemberCount > 0 && (
                    <div className="nc-broadcast-section">
                      <div className="nc-broadcast-info-row">
                        <div className="nc-broadcast-icon-wrap">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0">
                            <path d="M18 11v-1a2 2 0 0 0-2-2H4l-2 6h16a2 2 0 0 0 2-2v-1zM4 11h10M3 15l1-4"/>
                            <path d="M22 5L2 12.5" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="19" cy="5" r="3" fill="white"/>
                          </svg>
                        </div>
                        <div className="nc-broadcast-text">
                          <div className="nc-broadcast-title">Broadcast to Team</div>
                          <div className="nc-broadcast-subtitle">
                            Send a message to all {brokerageLinkedMemberCount} member{brokerageLinkedMemberCount !== 1 ? 's' : ''} (brokers + agents) in your firm
                          </div>
                        </div>
                      </div>
                      <button className="nc-broadcast-btn" onClick={handleComposeBrokerageBroadcast} disabled={isBroadcastingBrokerage}>
                        {isBroadcastingBrokerage ? 'Creating...' : 'Compose Broadcast'}
                      </button>
                    </div>
                  )}

                  {/* Step label */}
                  <div className="nc-step-label">Step 1: Select Transaction (Optional)</div>

                  <div className="nc-txn-list-scroll">
                    {/* General Chat option */}
                    <div className="nc-general-chat-item" onClick={() => selectTransactionForChat(null)}>
                      <div className="nc-txn-icon-wrap">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <div className="nc-txn-item-body">
                        <div className="nc-txn-item-name">General Chat (No Transaction)</div>
                        <div className="nc-txn-item-sub">Create a chat not linked to any transaction</div>
                      </div>
                    </div>

                    {/* Transaction list */}
                    {newChatTransactionsLoading ? (
                      <div className="new-chat-empty">Loading transactions...</div>
                    ) : newChatTransactions.length === 0 ? (
                      <div className="new-chat-empty" style={{ paddingTop: 8 }}>No active transactions</div>
                    ) : (
                      newChatTransactions.map(txn => {
                        const side = (txn.side || '').toUpperCase();
                        const txnLabel = txn.transactionName || txn.dealName || txn.propertyAddress || 'Transaction';
                        const txnNum = txn.transactionNumber || '';
                        return (
                          <div key={txn.id} className="nc-txn-row" onClick={() => selectTransactionForChat(txn)}>
                            <div className="nc-txn-home-wrap">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2D1B69" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                <polyline points="9 22 9 12 15 12 15 22"/>
                              </svg>
                            </div>
                            <div className="nc-txn-item-body">
                              <div className="nc-txn-item-name">
                                {txnLabel}
                                {side && (
                                  <span className={`nc-txn-side nc-txn-side-${side}`}> — {side === 'SALE' ? 'LISTING' : side}</span>
                                )}
                              </div>
                              {txnNum && <div className="nc-txn-item-num">{txnNum}</div>}
                            </div>
                            <div className="nc-txn-radio" />
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              ) : (
                /* ── STEP 2: Select Participants ── */
                <>
                  <div className="new-chat-header">
                    <button className="new-chat-back-btn" onClick={() => setNewChatStep('start')} title="Back">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </button>
                    <h2 className="new-chat-title">New Chat</h2>
                  </div>

                  {/* Transaction context banner */}
                  {selectedNewChatTxn && (
                    <div className="nc-txn-context-banner">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      <span>{selectedNewChatTxn.transactionName || selectedNewChatTxn.dealName || selectedNewChatTxn.propertyAddress || 'Transaction'}</span>
                    </div>
                  )}

                  {/* Selected user pills */}
                  {selectedNewChatUsers.length > 0 && (
                    <div className="new-chat-pills">
                      {selectedNewChatUsers.map(user => {
                        const id = user.firebaseUid || user.id;
                        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
                        return (
                          <div key={id} className="new-chat-pill">
                            <span className="new-chat-pill-name">{name}</span>
                            <button className="new-chat-pill-remove" onClick={() => handleToggleNewChatUser(user)}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Search */}
                  <div className="new-chat-search-wrapper">
                    <span className="new-chat-search-icon">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </span>
                    <input
                      className="new-chat-search-input"
                      type="text"
                      placeholder="Search users..."
                      value={newChatSearch}
                      onChange={e => setNewChatSearch(e.target.value)}
                    />
                    {newChatSearch && (
                      <button className="new-chat-search-clear" onClick={() => setNewChatSearch('')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* User list */}
                  <div
                    className="new-chat-user-list"
                    ref={newChatUserListRef}
                    onScroll={handleUserListScroll}
                  >
                    {newChatLoading ? (
                      <div className="new-chat-empty">Loading users...</div>
                    ) : filteredNewChatUsers.length === 0 ? (
                      <div className="new-chat-empty">
                        {newChatSearch ? `No users found for "${newChatSearch}"` : 'No users available'}
                      </div>
                    ) : (
                      filteredNewChatUsers.map(user => {
                        const id = user.firebaseUid || user.id;
                        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
                        const initials = getInitials(name);
                        const color = getAvatarColor(name);
                        const isSelected = selectedNewChatUsers.some(u => (u.firebaseUid || u.id) === id);
                        const role = (user.roles?.[0] || user.role || '');
                        const roleDisplay = role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase().replace(/_/g, ' ') : '';
                        return (
                          <div
                            key={id}
                            className={`new-chat-user-item${isSelected ? ' selected' : ''}`}
                            onClick={() => handleToggleNewChatUser(user)}
                          >
                            <div className={`new-chat-checkbox${isSelected ? ' checked' : ''}`}>
                              {isSelected && (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </div>
                            <div className="new-chat-avatar" style={{ backgroundColor: color }}>
                              {user.profilePhotoUrl ? (
                                <img src={user.profilePhotoUrl} alt={name} className="new-chat-avatar-img" />
                              ) : (
                                <span>{initials}</span>
                              )}
                            </div>
                            <div className="new-chat-user-info">
                              <span className="new-chat-user-name">{name}</span>
                              {user.email && <span className="new-chat-user-email">{user.email}</span>}
                              {roleDisplay && <span className="new-chat-user-role">{roleDisplay}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                    {newChatLoadingMore && (
                      <div className="new-chat-load-more-spinner">
                        <span className="btn-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                      </div>
                    )}
                  </div>

                  {/* Start Chat button */}
                  {selectedNewChatUsers.length > 0 && (
                    <div className="new-chat-action">
                      <button
                        className="new-chat-start-btn"
                        onClick={handleStartNewChat}
                        disabled={isCreatingChat}
                      >
                        {isCreatingChat
                          ? 'Creating...'
                          : selectedNewChatUsers.length === 1
                            ? 'Start Chat'
                            : `Create Group (${selectedNewChatUsers.length})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
          <>
          <div className="sidebar-header">
            <div className="sidebar-header-row">
              <h1 className="sidebar-title">Conversations</h1>
              <div className="sidebar-header-actions">
                <button className="sidebar-add-contact-btn" onClick={openAddContactModal} title="Add Contact">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <line x1="19" y1="8" x2="19" y2="14"/>
                    <line x1="22" y1="11" x2="16" y2="11"/>
                  </svg>
                  <span>Add Contact</span>
                </button>
                <button className="sidebar-compose-btn" onClick={openNewChat} title="New Chat">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="search-wrapper">
            <div className="search-row">
              <div className="search-input-wrapper">
                <span className="search-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  type="text"
                  className="conversation-search"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="conv-filter-wrapper" ref={filterDropdownRef}>
                <button
                  className={`search-filter-btn${showFilterDropdown ? ' active' : ''}${activeFilter !== 'all' ? ' filtered' : ''}`}
                  title="Filter"
                  onClick={() => setShowFilterDropdown(prev => !prev)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="6" y1="12" x2="18" y2="12"/>
                    <line x1="9" y1="18" x2="15" y2="18"/>
                  </svg>
                  {activeFilter !== 'all' && <span className="conv-filter-dot" />}
                </button>

                {showFilterDropdown && (
                  <div className="conv-filter-dropdown">
                    {[
                      {
                        key: 'all', label: 'All',
                        // Chat bubble with two text lines inside (Symbol (1).png)
                        icon: (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            <line x1="9" y1="9" x2="15" y2="9"/>
                            <line x1="9" y1="12" x2="13" y2="12"/>
                          </svg>
                        )
                      },
                      {
                        key: 'unread', label: 'Unread',
                        // Rounded square with small filled square badge at top-right corner (Symbol.png)
                        icon: (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 4H6a3 3 0 0 0-3 3v11a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3v-8"/>
                            <rect x="14" y="2" width="7" height="7" rx="1.5" fill="currentColor" stroke="none"/>
                          </svg>
                        )
                      },
                      {
                        key: 'pinned', label: 'Pinned',
                        // Pin/thumbtack icon
                        icon: (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="17" x2="12" y2="22"/>
                            <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
                          </svg>
                        )
                      },
                    ].map(({ key, label, icon }) => (
                      <button
                        key={key}
                        className={`conv-filter-option${activeFilter === key ? ' active' : ''}`}
                        onClick={() => { setActiveFilter(key); setShowFilterDropdown(false); }}
                      >
                        <span className="conv-filter-check">
                          {activeFilter === key && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </span>
                        <span className="conv-filter-option-icon">{icon}</span>
                        <span className="conv-filter-option-label">{label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="conversation-list">
            {/* Ria AI — always pinned at top */}
            {SHOW_RIA_SIDEBAR_ITEMS && (!searchQuery.trim() || 'ria'.includes(searchQuery.toLowerCase())) && (
              <div
                className={`conversation-item ria-sidebar-item ${selectedConversation === RIA_CONVERSATION_ID ? 'active' : ''}`}
                onClick={handleRiaChatSelect}
              >
                <div className="conversation-avatar">
                  <img src={logo} alt="Ria" className="avatar-circle ria-sidebar-avatar" />
                </div>
                <div className="conversation-info">
                  <div className="conversation-header-row">
                    <h3 className="conversation-name">Ria</h3>
                    <span className="conversation-time"></span>
                  </div>
                  <div className="conv-item-bottom-row">
                    <p className="conversation-preview">Your Real Estate Intelligent Assistant</p>
                    <div className="conv-item-actions">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="#6B4EFF" stroke="#6B4EFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-5.18 3.25L8 14.14 3 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ria Weekly */}
            {SHOW_RIA_SIDEBAR_ITEMS && (!searchQuery.trim() || 'ria weekly'.includes(searchQuery.toLowerCase())) && (
              <div
                className={`conversation-item ria-sidebar-item ${selectedConversation === RIA_WEEKLY_ID ? 'active' : ''}`}
                onClick={handleRiaWeeklySelect}
              >
                <div className="conversation-avatar">
                  <img src={logo} alt="Ria Weekly" className="avatar-circle ria-sidebar-avatar" />
                </div>
                <div className="conversation-info">
                  <div className="conversation-header-row">
                    <h3 className="conversation-name">Ria Weekly</h3>
                    <span className="conversation-time"></span>
                  </div>
                  <div className="conv-item-bottom-row">
                    <p className="conversation-preview">Weekly conversation history</p>
                    <div className="conv-item-actions">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="#6B4EFF" stroke="#6B4EFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-5.18 3.25L8 14.14 3 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ria Monthly */}
            {SHOW_RIA_SIDEBAR_ITEMS && (!searchQuery.trim() || 'ria monthly'.includes(searchQuery.toLowerCase())) && (
              <div
                className={`conversation-item ria-sidebar-item ${selectedConversation === RIA_MONTHLY_ID ? 'active' : ''}`}
                onClick={handleRiaMonthlySelect}
              >
                <div className="conversation-avatar">
                  <img src={logo} alt="Ria Monthly" className="avatar-circle ria-sidebar-avatar" />
                </div>
                <div className="conversation-info">
                  <div className="conversation-header-row">
                    <h3 className="conversation-name">Ria Monthly</h3>
                    <span className="conversation-time"></span>
                  </div>
                  <div className="conv-item-bottom-row">
                    <p className="conversation-preview">Monthly conversation history</p>
                    <div className="conv-item-actions">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="#6B4EFF" stroke="#6B4EFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-5.18 3.25L8 14.14 3 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="no-conversation-selected" style={{ height: 'auto', padding: '40px 24px' }}>
                <p>Loading conversations...</p>
              </div>
            ) : error ? (
              <div className="no-conversation-selected" style={{ height: 'auto', padding: '40px 24px' }}>
                <p>{error}</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="no-conversation-selected" style={{ height: 'auto', padding: '40px 24px' }}>
                <p>{searchQuery ? 'No matching conversations' : 'No conversations yet'}</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`conversation-item ${selectedConversation === conversation.id ? 'active' : ''}`}
                  onClick={() => handleConversationSelect(conversation)}
                >
                  <div className="conversation-avatar">
                    {conversation.isGroupChat ? (
                      <div className="avatar-circle" style={{ backgroundColor: conversation.groupColor }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                      </div>
                    ) : conversation.profilePhotoUrl ? (
                      <img
                        src={conversation.profilePhotoUrl}
                        alt={conversation.name}
                        className="avatar-circle"
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="avatar-circle"
                        style={{ backgroundColor: conversation.avatarColor }}
                      >
                        {conversation.initials}
                      </div>
                    )}
                  </div>

                  <div className="conversation-info">
                    <div className="conversation-header-row">
                      <h3 className="conversation-name">{conversation.name}</h3>
                      <span className="conversation-time">{conversation.time}</span>
                    </div>
                    <div className="conv-item-bottom-row">
                      <p className="conversation-preview">{conversation.lastMessage}</p>
                      <div className="conv-item-actions">
                        {conversation.unread > 0 && (
                          <span className="unread-badge">{conversation.unread}</span>
                        )}
                        <button
                          type="button"
                          className="conv-delete-btn"
                          title="Delete chat"
                          aria-label="Delete chat"
                          onClick={(e) => requestDeleteChat(conversation, e)}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                        <svg
                          className={`conv-star-icon${pinnedIds.has(conversation.id) ? ' starred' : ''}`}
                          width="13" height="13" viewBox="0 0 24 24"
                          fill={pinnedIds.has(conversation.id) ? '#6B4EFF' : 'none'}
                          stroke={pinnedIds.has(conversation.id) ? '#6B4EFF' : 'currentColor'}
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          title={pinnedIds.has(conversation.id) ? 'Unpin' : 'Pin'}
                          onClick={(e) => togglePin(e, conversation.id)}
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                        {/* Notes — private to the viewer, attached to this conversation
                            thread. Same record shown in My Notes — not a copy. */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <NotesPanel linkType="CONVERSATION" linkId={conversation.id} linkLabel={conversation.name} popover />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          </>
          )}
        </div>

        {/* Right Side - Active Conversation */}
        <div className="conversation-main">
          {activeConversation ? (
            <>
              {/* Conversation Header */}
              <div className="conversation-header">
                {selectionMode ? (
                  <div className="conv-selection-toolbar">
                    <button className="conv-selection-cancel" onClick={handleCancelSelection} title="Cancel">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                    <span className="conv-selection-count">{selectedMessageIds.size} selected</span>
                    <div className="conv-selection-actions">
                      <button
                        className="conv-selection-action-btn"
                        disabled={selectedMessageIds.size !== 1}
                        onClick={handleReply}
                        title="Reply"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
                        </svg>
                      </button>
                      <button
                        className="conv-selection-action-btn"
                        disabled={selectedMessageIds.size === 0}
                        onClick={handleForward}
                        title="Forward"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                        </svg>
                      </button>
                      <button
                        className="conv-selection-action-btn"
                        disabled={selectedMessageIds.size === 0}
                        onClick={handleDeleteMessagesClick}
                        title="Delete"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                <>
                {/* Back button — mobile only, returns to the conversation list */}
                <button className="mobile-back-btn" onClick={() => setMobileChatOpen(false)} title="Back to conversations" aria-label="Back to conversations">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>

                {/* Compose icon — left */}
                {/* <button className="header-compose-btn" title="New conversation" onClick={openNewChat}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button> */}

                {/* Center — avatar + name */}
                <div className="header-center">
                  {activeConversation.isRia ? (
                    <img src={logo} alt="Ria" className="avatar-circle ria-header-avatar" style={{ width: 40, height: 40 }} />
                  ) : activeConversation.isGroupChat ? (
                    <div
                      className="avatar-circle"
                      style={{ backgroundColor: activeConversation.groupColor, width: 40, height: 40, flexShrink: 0 }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                      </svg>
                    </div>
                  ) : activeConversation.profilePhotoUrl ? (
                    <img
                      src={activeConversation.profilePhotoUrl}
                      alt={activeConversation.name}
                      className="avatar-circle"
                      style={{ objectFit: 'cover', width: 40, height: 40, fontSize: 14 }}
                    />
                  ) : (
                    <div
                      className="avatar-circle"
                      style={{ backgroundColor: activeConversation.avatarColor, width: 40, height: 40, fontSize: 14 }}
                    >
                      {activeConversation.initials}
                    </div>
                  )}
                  <div className="header-name-group">
                    <h2 className="header-name">{activeConversation.name}</h2>
                    {activeConversation.isGroupChat && (activeConversation.participants?.length ?? 0) > 0 && (
                      <p className="header-participant-count">
                        {activeConversation.participants.length} participant{activeConversation.participants.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>

                {/* Group members button — visible for all group chats, mirrors mobile account-group icon */}
                {activeConversation.isGroupChat && (
                  <button className="header-group-btn" title="View participants" onClick={openMembersModal}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                  </button>
                )}

                {/* Plus button — only for agents/brokers on 1-on-1/group chats; hidden for transaction chats and broadcast chats (mirrors mobile canManageParticipants) */}
                {canManageParticipants && !activeConversation.transactionId && activeConversation.name !== 'Broadcast Message' && activeConversation.name !== 'Brokerage Broadcast' && (
                  <button className="header-plus-btn" title="Add participant" onClick={openAddMemberPanel}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </button>
                )}
                </>
                )}
              </div>

              {/* Messages Area */}
              <div className="messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
                {isLoadingMoreRia && (
                  <div className="ria-load-more-bar">
                    <div className="ria-load-more-spinner" />
                    <span>Loading older messages…</span>
                  </div>
                )}
                {messagesLoading ? (
                  <div className="no-conversation-selected">
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="no-conversation-selected">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.reduce((acc, message, idx) => {
                    const dateLabel = getDateLabel(message.createdAt);
                    const prevDateLabel = idx > 0 ? getDateLabel(messages[idx - 1].createdAt) : null;
                    if (dateLabel && dateLabel !== prevDateLabel) {
                      acc.push(
                        <div key={`sep-${idx}`} className="message-date-separator">
                          <span className="message-date-label">{dateLabel}</span>
                        </div>
                      );
                    }
                    if (message.messageType === 'SYSTEM') {
                      acc.push(
                        <div key={message.id} className="system-message">
                          <div className="system-message-row">
                            <span className="system-message-dash" />
                            <span className="system-message-text">{formatSystemMessage(message.text)}</span>
                            <span className="system-message-dash" />
                          </div>
                        </div>
                      );
                      return acc;
                    }
                    // Selectable for Reply/Forward/Delete — system messages and already
                    // deleted-for-everyone messages can never enter selection (mirrors
                    // mobile MessageRow's disabled={isSystemMessage || isDeletedForEveryone}).
                    const isSelectable = message.messageType !== 'SYSTEM' && !message.deletedForEveryone && !activeConversation?.isRia;
                    const isSelected = selectedMessageIds.has(message.id);
                    acc.push(
                    <div
                      key={message.id}
                      className={`message ${message.isSent ? 'message-sent' : `message-received${message.isRia ? ' ria-message' : ''}`}${selectionMode && isSelected ? ' message-row-selected' : ''}`}
                      onClick={() => { if (selectionMode && isSelectable) toggleMessageSelection(message.id); }}
                    >
                      {selectionMode && isSelectable && (
                        <div
                          className="message-select-checkbox"
                          onClick={(e) => { e.stopPropagation(); toggleMessageSelection(message.id); }}
                        >
                          <input type="checkbox" checked={isSelected} readOnly />
                        </div>
                      )}
                      {!message.isSent && (
                        <div className="message-avatar">
                          {message.isRia ? (
                            <img src={logo} alt="Ria" className="avatar-circle-small ria-msg-avatar" />
                          ) : userCache[message.senderId]?.profilePhotoUrl ? (
                            <img
                              src={userCache[message.senderId].profilePhotoUrl}
                              alt={message.senderName || userCache[message.senderId]?.name || ''}
                              className="avatar-circle-small"
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="avatar-circle-small"
                              style={{ backgroundColor: getAvatarColor(
                                message.senderName || userCache[message.senderId]?.name || ''
                              ) }}
                            >
                              {getInitials(message.senderName || userCache[message.senderId]?.name || '')}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="message-content">
                        <div className="message-sender">
                          {message.isSent
                            ? 'You'
                            : (message.senderName || userCache[message.senderId]?.name || 'Unknown')}
                          , {message.time}
                        </div>
                        {message.deletedForEveryone ? (
                          <div className="message-bubble message-bubble-deleted">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                            <span className="message-deleted-text">
                              {message.isSent ? 'You deleted this message' : 'This message was deleted'}
                            </span>
                          </div>
                        ) : (
                          <>
                            {message.forwarded && (
                              <div className="conv-forwarded-badge">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                </svg>
                                <span>Forwarded{message.originalSenderName ? ` from ${message.originalSenderName}` : ''}</span>
                              </div>
                            )}
                            {message.replyTo && (
                              <div className={`conv-reply-quote ${message.isSent ? 'sent' : 'received'}`}>
                                <span className="conv-reply-quote-sender">{message.replyTo.senderName}</span>
                                <span className="conv-reply-quote-text">{message.replyTo.content || 'Message'}</span>
                              </div>
                            )}
                            {message.messageType === 'PROPERTY' && message.propertyData ? (
                              <PropertyMessageCard
                                propertyData={message.propertyData}
                                onViewProperty={(mlsId) => navigate(`/property/${mlsId}`)}
                              />
                            ) : (
                              <div className="message-bubble">
                                {message.attachments?.map((att, i) => {
                                  const mime = att.mimeType || '';
                                  const url = att.fileUrl || att.previewUrl || '';
                                  if (mime.startsWith('image/')) {
                                    // Wrapped in a link so the recipient can open/download it —
                                    // the bare <img> had no click handler despite the pointer
                                    // cursor CSS (.msg-attachment-img) implying one was intended.
                                    return (
                                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="msg-attachment-img-link">
                                        <img src={url} alt={att.fileName} className="msg-attachment-img" />
                                      </a>
                                    );
                                  }
                                  if (mime.startsWith('video/')) {
                                    return (
                                      <div key={i} className="msg-attachment-media-wrap">
                                        <video src={url} controls className="msg-attachment-video" />
                                        <a href={url} target="_blank" rel="noopener noreferrer" className="msg-attachment-download-link">Download</a>
                                      </div>
                                    );
                                  }
                                  if (mime.startsWith('audio/')) {
                                    return (
                                      <div key={i} className="msg-attachment-media-wrap">
                                        <audio src={url} controls className="msg-attachment-audio" />
                                        <a href={url} target="_blank" rel="noopener noreferrer" className="msg-attachment-download-link">Download</a>
                                      </div>
                                    );
                                  }
                                  return (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="msg-attachment-file">
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      <span className="msg-attachment-name">{att.fileName}</span>
                                      {att.fileSize > 0 && <span className="msg-attachment-size">{formatFileSize(att.fileSize)}</span>}
                                    </a>
                                  );
                                })}
                                {message.linkPreview?.title && (
                                  <a
                                    href={message.linkPreview.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`conv-link-preview-card ${message.isSent ? 'sent' : 'received'}`}
                                  >
                                    {message.linkPreview.imageUrl && (
                                      <img src={message.linkPreview.imageUrl} alt="" className="conv-link-preview-img" />
                                    )}
                                    <div className="conv-link-preview-content">
                                      {message.linkPreview.siteName && <div className="conv-link-preview-site">{message.linkPreview.siteName}</div>}
                                      <div className="conv-link-preview-title">{message.linkPreview.title}</div>
                                      {message.linkPreview.description && <div className="conv-link-preview-desc">{message.linkPreview.description}</div>}
                                    </div>
                                  </a>
                                )}
                                {message.text && (
                                  <span className="message-text-with-status">
                                    {message.text}
                                    {/* WhatsApp-style status indicator for sent messages */}
                                    {message.isSent && !message.deletedForEveryone && message.status && message.status !== 'failed' && (
                                      <MessageStatusIndicator status={message.status} size={14} />
                                    )}
                                  </span>
                                )}
                                {message.isRia && message.noteLinks?.length > 0 && (
                                  <RiaNoteLinks notes={message.noteLinks} />
                                )}
                                {/* Retry button for failed messages */}
                                {message.isSent && message.status === 'failed' && (
                                  <button
                                    className="message-retry-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRetryMessage(message.id);
                                    }}
                                    title="Tap to retry sending"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                                    </svg>
                                    <span>Failed to send. Tap to retry</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {!selectionMode && isSelectable && (
                        <button
                          className="message-actions-trigger"
                          title="Reply, forward or delete"
                          onClick={(e) => { e.stopPropagation(); handleStartSelection(message.id); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    );
                    return acc;
                  }, [])
                )}
                {/* Typing indicator - shown when other users are typing */}
                {typingUsers.length > 0 && !activeConversation?.isRia && (
                  <TypingIndicator
                    typingUsers={typingUsers}
                    isGroupChat={activeConversation?.isGroupChat}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                {(isFetchingPreview || linkPreview) && !activeConversation?.isRia && (
                  <div className="conv-compose-link-preview">
                    {isFetchingPreview && !linkPreview ? (
                      <div className="conv-link-preview-loading">
                        <div className="conv-link-preview-spinner"></div>
                        <span>Loading preview...</span>
                      </div>
                    ) : linkPreview && (
                      <a href={linkPreview.url} target="_blank" rel="noopener noreferrer" className="conv-link-preview-card">
                        {linkPreview.imageUrl && (
                          <img src={linkPreview.imageUrl} alt="" className="conv-link-preview-img" />
                        )}
                        <div className="conv-link-preview-content">
                          {linkPreview.siteName && <div className="conv-link-preview-site">{linkPreview.siteName}</div>}
                          <div className="conv-link-preview-title">{linkPreview.title}</div>
                          {linkPreview.description && <div className="conv-link-preview-desc">{linkPreview.description}</div>}
                        </div>
                      </a>
                    )}
                    <button className="conv-link-preview-dismiss" onClick={handleDismissLinkPreview} title="Dismiss">✕</button>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={handleFileSelect}
                />
                {pendingAttachment && (
                  <div className="pending-attachment-bar">
                    {pendingAttachment.previewUrl ? (
                      <img src={pendingAttachment.previewUrl} alt="preview" className="pending-attach-thumb" />
                    ) : (
                      <div className="pending-attach-file">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span>{pendingAttachment.name}</span>
                      </div>
                    )}
                    <span className="pending-attach-size">{formatFileSize(pendingAttachment.size)}</span>
                    <button className="pending-attach-remove" onClick={removePendingAttachment} title="Remove">✕</button>
                  </div>
                )}
                {replyToMessage && (
                  <div className="conv-reply-preview-bar">
                    <div className="conv-reply-preview-accent" />
                    <div className="conv-reply-preview-content">
                      <span className="conv-reply-preview-sender">
                        Replying to {replyToMessage.senderId === auth.currentUser?.uid ? 'yourself' : (replyToMessage.senderName || 'Unknown')}
                      </span>
                      <span className="conv-reply-preview-text">
                        {replyToMessage.text || (replyToMessage.attachments?.length ? 'Attachment' : 'Message')}
                      </span>
                    </div>
                    <button className="conv-reply-preview-close" onClick={handleCancelReply} title="Cancel reply">✕</button>
                  </div>
                )}
                <AskRiaInputBar
                  value={messageInput}
                  onChange={handleInputChange}
                  onSend={handleSendMessage}
                  placeholder={activeConversation?.isRia ? 'Ask Ria anything...' : `Message ${activeConversation?.name || 'conversation'}...`}
                  loading={isUploadingAttachment}
                  disabled={isUploadingAttachment}
                  onAddClick={() => fileInputRef.current?.click()}
                  addButtonTitle="Attach file"
                  addButtonDisabled={isUploadingAttachment}
                />
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <p>Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>

      {/* Group Members Modal */}
      {showMembersModal && (
        <div className="new-chat-modal-overlay" onClick={() => setShowMembersModal(false)}>
          <div className="add-member-modal" onClick={e => e.stopPropagation()}>
            <div className="add-member-modal-header">
              <div>
                <h3 className="new-chat-modal-title">
                  Participants
                  {!membersLoading && (
                    <span className="members-count-badge">
                      {membersData.length || activeConversation?.participants?.length || 0}
                    </span>
                  )}
                </h3>
              </div>
              <button className="add-member-close-btn" onClick={() => setShowMembersModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="add-member-user-list">
              {membersLoading ? (
                <div className="add-member-empty">Loading participants…</div>
              ) : membersData.length === 0 ? (
                <div className="add-member-empty">No participants found</div>
              ) : (
                membersData.map(member => {
                  const isCurrentUser = member.uid === auth.currentUser?.uid;
                  return (
                    <div key={member.uid} className="add-member-user-item">
                      <div className="new-chat-avatar" style={{ backgroundColor: getAvatarColor(member.name), flexShrink: 0 }}>
                        {member.profilePhotoUrl ? (
                          <img src={member.profilePhotoUrl} alt={member.name} className="new-chat-avatar-img" />
                        ) : (
                          <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
                            {getInitials(member.name)}
                          </span>
                        )}
                      </div>
                      <div className="add-member-user-info">
                        <div className="members-name-row">
                          <span className="add-member-user-name">{member.name}</span>
                          {isCurrentUser && <span className="member-you-tag">You</span>}
                        </div>
                        {member.email && (
                          <span className="add-member-user-email">{member.email}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forward Message(s) Modal — mirrors mobile's "Forward to..." picker */}
      {showForwardPicker && (
        <div className="new-chat-modal-overlay" onClick={handleCancelForward}>
          <div className="add-member-modal" onClick={e => e.stopPropagation()}>
            <div className="add-member-modal-header">
              <div>
                <h3 className="new-chat-modal-title">Forward to...</h3>
              </div>
              <button className="add-member-close-btn" onClick={handleCancelForward}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="add-member-user-list">
              {conversationList.filter(c => c.id !== selectedConversation).length === 0 ? (
                <div className="add-member-empty">No other conversations to forward to</div>
              ) : (
                conversationList
                  .filter(c => c.id !== selectedConversation)
                  .map(conv => (
                    <div
                      key={conv.id}
                      className="add-member-user-item"
                      onClick={() => handleForwardToConversation(conv)}
                      style={{ cursor: isForwarding ? 'default' : 'pointer', opacity: isForwarding ? 0.6 : 1 }}
                    >
                      <div className="new-chat-avatar" style={{ backgroundColor: conv.isGroupChat ? conv.groupColor : conv.avatarColor, flexShrink: 0 }}>
                        {conv.profilePhotoUrl ? (
                          <img src={conv.profilePhotoUrl} alt={conv.name} className="new-chat-avatar-img" />
                        ) : (
                          <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
                            {conv.initials}
                          </span>
                        )}
                      </div>
                      <div className="add-member-user-info">
                        <span className="add-member-user-name">{conv.name}</span>
                        <span className="add-member-user-email">{conv.lastMessage}</span>
                      </div>
                    </div>
                  ))
              )}
              {isForwarding && <div className="add-member-empty">Forwarding…</div>}
            </div>
          </div>
        </div>
      )}

      {/* Delete Message(s) Confirmation — web equivalent of mobile's native Alert.alert
          with Cancel / Delete for Me / Delete for Everyone (offered only when eligible) */}
      {showDeleteConfirm && (
        <div className="new-chat-modal-overlay" onClick={handleCancelDelete}>
          <div className="conv-delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="conv-delete-confirm-title">
              Delete {selectedMessageIds.size > 1 ? `${selectedMessageIds.size} messages` : 'message'}?
            </h3>
            <div className="conv-delete-confirm-actions">
              <button
                className="conv-delete-confirm-btn"
                onClick={() => handleConfirmDelete('me')}
                disabled={isDeleting}
              >
                Delete for Me
              </button>
              {canDeleteSelectedForEveryone() && (
                <button
                  className="conv-delete-confirm-btn conv-delete-confirm-btn-destructive"
                  onClick={() => handleConfirmDelete('everyone')}
                  disabled={isDeleting}
                >
                  Delete for Everyone
                </button>
              )}
              <button
                className="conv-delete-confirm-btn conv-delete-confirm-cancel"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member to Chat Modal */}
      {showAddMemberPanel && (
        <div className="new-chat-modal-overlay" onClick={() => setShowAddMemberPanel(false)}>
          <div className="add-member-modal" onClick={e => e.stopPropagation()}>
            <div className="add-member-modal-header">
              <div>
                <h3 className="new-chat-modal-title">Add to Chat</h3>
                <p className="new-chat-modal-subtitle">
                  {activeConversation?.transactionId ? 'Transaction Participants' : 'Available Users'}
                </p>
              </div>
              <button className="add-member-close-btn" onClick={() => setShowAddMemberPanel(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="search-input-wrapper add-member-search">
              <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="conversation-search"
                type="text"
                placeholder="Search by name or email..."
                value={addMemberSearch}
                onChange={e => setAddMemberSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="add-member-user-list">
              {addMemberLoading ? (
                <div className="add-member-empty">Loading users…</div>
              ) : filteredAddMemberUsers.length === 0 ? (
                <div className="add-member-empty">
                  {addMemberSearch ? 'No users match your search' : 'No users available to add'}
                </div>
              ) : (
                filteredAddMemberUsers.map(user => (
                  <div key={user.id} className="add-member-user-item">
                    <div className="new-chat-avatar" style={{ backgroundColor: getAvatarColor(user.name), flexShrink: 0 }}>
                      {user.profilePhotoUrl ? (
                        <img src={user.profilePhotoUrl} alt={user.name} className="new-chat-avatar-img" />
                      ) : (
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
                          {getInitials(user.name)}
                        </span>
                      )}
                    </div>
                    <div className="add-member-user-info">
                      <span className="add-member-user-name">{user.name}</span>
                      {user.email && <span className="add-member-user-email">{user.email}</span>}
                      {user.role && (
                        <span className="add-member-user-role">
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase()}
                        </span>
                      )}
                    </div>
                    <button
                      className="add-member-add-btn"
                      onClick={() => handleAddMember(user)}
                      disabled={isAddingMember}
                      title={`Add ${user.name}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group Name Modal */}
      {/* ── Add Contact Modal ─────────────────────────────────────────── */}
      {showAddContactModal && (
        <div className="ac-modal-overlay" onClick={closeAddContactModal}>
          <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="ac-modal-title">Add a contact</h2>
            <p className="ac-modal-sub">Enter their email to find them on Rialty. To add someone not yet on the platform, send an invite.</p>

            <div className="ac-email-row">
              <input
                className="ac-email-input"
                type="email"
                placeholder="e.g. john@remax.com"
                value={addContactEmail}
                onChange={(e) => {
                  setAddContactEmail(e.target.value);
                  if (addContactState !== 'idle' && addContactState !== 'loading') {
                    setAddContactState('idle');
                    setAddContactResult(null);
                    setAddContactError('');
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddContact(); }}
                disabled={addContactState === 'loading' || addContactState === 'success'}
                autoFocus
              />
              {addContactState !== 'success' && (
                <button
                  className="ac-add-btn"
                  onClick={handleAddContact}
                  disabled={!addContactEmail.trim() || addContactState === 'loading'}
                >
                  {addContactState === 'loading'
                    ? <><span className="btn-spinner" style={{ width: 12, height: 12, borderWidth: 2 }}></span><span>Adding…</span></>
                    : 'Add'}
                </button>
              )}
            </div>

            {/* Success */}
            {addContactState === 'success' && addContactResult && (
              <div className="ac-success-card">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span><strong>{addContactResult.name}</strong> has been added to your contacts. You can now start a conversation.</span>
              </div>
            )}

            {/* Not found */}
            {addContactState === 'notfound' && (
              <div className="ac-notfound-card">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>This person is not on Rialty yet. Would you like to send them an invite?</span>
              </div>
            )}

            {/* Error */}
            {addContactState === 'error' && (
              <div className="ac-error-card">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>{addContactError}</span>
              </div>
            )}

            {/* Footer actions */}
            <div className="ac-modal-footer">
              {addContactState === 'success' ? (
                <>
                  <button className="ac-cancel-btn" onClick={closeAddContactModal}>Close</button>
                  <button className="ac-cta-btn" onClick={() => { closeAddContactModal(); openNewChat(); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    Start chat
                  </button>
                </>
              ) : addContactState === 'notfound' ? (
                <>
                  <button className="ac-cancel-btn" onClick={closeAddContactModal}>Cancel</button>
                  <button className="ac-cta-btn ac-invite-btn" onClick={() => {
                    const email = addContactEmail.trim();
                    closeAddContactModal();
                    navigate('/invite-someone', { state: { prefillEmail: email } });
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    Send invite
                  </button>
                </>
              ) : (
                <button
                  className="ac-cancel-btn"
                  onClick={closeAddContactModal}
                  disabled={addContactState === 'loading'}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showGroupNameModal && (
        <div className="new-chat-modal-overlay" onClick={() => { setShowGroupNameModal(false); setIsCreatingChat(false); }}>
          <div className="new-chat-modal" onClick={e => e.stopPropagation()}>
            <h3 className="new-chat-modal-title">Group Name</h3>
            <p className="new-chat-modal-subtitle">Optional — leave empty to auto-generate</p>
            <input
              className="new-chat-modal-input"
              type="text"
              placeholder="Enter group name..."
              value={newChatGroupName}
              onChange={e => setNewChatGroupName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doCreateChat(newChatGroupName); }}
              autoFocus
            />
            <div className="new-chat-modal-actions">
              <button
                className="new-chat-modal-cancel"
                onClick={() => { setShowGroupNameModal(false); setIsCreatingChat(false); setNewChatGroupName(''); }}
              >
                Cancel
              </button>
              <button
                className="new-chat-modal-create"
                onClick={() => doCreateChat(newChatGroupName)}
                disabled={isCreatingChat}
              >
                {isCreatingChat ? 'Creating...' : newChatGroupName.trim() ? 'Create' : 'Skip'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete chat confirmation */}
      {deleteChatConfirm && (
        <div className="conv-delete-chat-overlay" onClick={closeDeleteChatConfirm}>
          <div className="conv-delete-chat-modal" onClick={e => e.stopPropagation()}>
            <div className="conv-delete-chat-title">Delete chat</div>
            <div className="conv-delete-chat-desc">
              Delete your copy of the chat with <strong>{deleteChatConfirm.name}</strong>?
              The other person will still see the messages.
            </div>
            <div className="conv-delete-chat-actions">
              <button
                className="conv-delete-chat-btn conv-delete-chat-yes"
                onClick={confirmDeleteChat}
                disabled={isDeletingChat}
              >
                {isDeletingChat ? 'Deleting…' : 'Delete'}
              </button>
              <button
                className="conv-delete-chat-btn conv-delete-chat-no"
                onClick={closeDeleteChatConfirm}
                disabled={isDeletingChat}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationPage;
